import React, { Component } from "react";
import ReactDOM from "react-dom";
import { geojson as fgb } from "flatgeobuf";
import { Map as ReactMap, Source, Layer, Popup } from "react-map-gl";
import { withRouter } from "./withRouter";
import mapboxgl from "mapbox-gl";
import MapToolbar from "./MapToolbar";
import { tableFromIPC } from "apache-arrow";
import {
  clamp01,
  hexToHsl,
  hslToHex,
  makeHeatScale,
  make2pointHeatScale,
  buildLabelsAndColors, baseColorForLikertKey
} from "./utils/colorScale";
import { DEMO_LABELS } from "./utils/demographicLabels";

import {
  normalizeId,
  stateFipsToName,
  rowsFromTable
} from "./utils/idUtils";
import { THEMES } from "./utils/themeRegistry";
import SelectionContext from './state/SelectionContext';
import { ALLOWED_LEVELS, getSelectableLevels, allowNationalStatelegForTheme } from './utils/geoLevels';
import {
  getFeatureCentroid as _getFeatureCentroid,
  getFeatureDisplayName as _getFeatureDisplayName,
  normalizeAreaIdForLevel as _normalizeAreaIdForLevel,
  findFeatureForAreaId as _findFeatureForAreaId,
  getConcentrationSet as _getConcentrationSet,
  processGeoFeatures as _processGeoFeatures,
} from "./utils/mapPage/geoUtils";
import { getAggregatesForSidebar as _getAggregatesForSidebar } from "./utils/mapPage/getAggregatesForSidebar";
import { computeRestyle as _computeRestyle } from "./utils/mapPage/restyle";
import { computeAudienceInsight as _computeAudienceInsight } from "./utils/mapPage/fetchTopDemographicInsight";
import { tableFromIPC as _tableFromIPC } from "apache-arrow";
import { rowsFromTable as _rowsFromTable } from "./utils/idUtils";
import { schemaTypeFor, isTrackingSchema } from "./utils/schemaTypes";
import { cdFgbPathFor, cdVersionFor, cdVersionInfoFor } from "./utils/cdVersion";

const MAPBOX_TOKEN =
  "pk.eyJ1IjoibXppZW1pYW4iLCJhIjoiY2xhOXdpc2h5MDI1MzNxbG1wdzcxOWNlZSJ9.Km0c9-jUUlcBs6ByXYbYZA";

const MAINLAND_BOUNDS = [
  [-125, 24],
  [-66, 49],
];
const MAP_STYLE = "mapbox://styles/mapbox/light-v11"; 

const JOIN_KEY_MAP = {
  states:        { shapefile: "STATEFP" },
  counties:      { shapefile: "GEOID" },
  cd:            { shapefile: "GEOID" },
  zipcodes:      { shapefile: "GEOID20" },
  statesenate:   { shapefile: "GEOID" },
  statehouse:    { shapefile: "GEOID" },
  media_market:  { shapefile: "Key" },
};



class MapPage extends Component {
  static contextType = SelectionContext;

  constructor(props) {
    
    super(props);
    this.state = {
      selectedStateCode: null,
      geoData: null,
      styledGeo: null,
      metricRows: null, 
      metricRowsStates: null,
      metricRowsState: null,
      metricRowsStatesUnfiltered: null,
      metricRowsUnfiltered: null,
      loading: false,
      compareRowsLatest: null,
      compareRowsPrev: null,
      compareMetricRows: null,
      compareWavePair: null,
      compareArrows: null,
      hoverInfo: null,
      showArrows: true,
      styleLoaded: false,
      selectedStateBounds: null,
      selectedFeature: null,
      ranked: [],
      geoPreloadStatus: {},
    };
    
    this.mapRef = React.createRef();
    this._inflight = null;
    if (props.onInstance) props.onInstance(this);
    this._lastKey = null;
    this._lastAt = 0;
    this._cache = new Map();
    this._cacheMax = 25;
    this._cacheTtlMs = 15000;
    this._insightCache = new Map();
    this._insightCacheTtlMs = 60000;


    
  }

  componentDidMount() {
    this.loadGeo("states", null, { doFit: true });
    this.fetchMetrics("states", null);
    this.fetchSecondaryMetrics("states", null)
    this.fetchStatesBaseline();          // baseline: one row per state
    this.fetchStatesBaselineUnfiltered(); // baseline without demo filters
    this.fetchUnfilteredTotals("states", null);
    this._preloadAllGeoLevels();
  }

  _preloadAllGeoLevels = async () => {
    const allowed = ALLOWED_LEVELS[this.props.selectedSection] || ALLOWED_LEVELS.default;
    // states is handled by the normal componentDidMount load
    // zipcodes need a stateCode so can't be preloaded nationally
    const toPreload = this.props.geoPreloadOrder
      ? this.props.geoPreloadOrder.filter(l => allowed.includes(l))
      : allowed.filter(l => l !== "states" && l !== "zipcodes");
    if (!toPreload.length) return;

    const initialStatus = { states: "loading" };
    toPreload.forEach(l => { initialStatus[l] = "loading"; });
    this.setState({ geoPreloadStatus: initialStatus });

    // Wait for the initial states load and metrics to settle before hogging bandwidth
    await new Promise(r => setTimeout(r, 1500));

    for (const level of toPreload) {
      await this.loadGeo(level, null, { doFit: false, backgroundOnly: true });
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevState.loading !== this.state.loading) {
      this.props.onLoadingChange?.(this.state.loading);
    }
    const prevFilters =
      (typeof prevProps.filters === "object" && prevProps.filters) || {
        gender: prevProps.genderFilter ?? null,
        age: prevProps.ageFilter ?? null,
        ethnicity: prevProps.ethnicityFilter ?? null,
        party: prevProps.partyFilter ?? null,
        education: prevProps.educationFilter ?? null,
        vote24:  prevProps.vote24Filter ?? null,
      };
    const curFilters = this.getFilters();
    const filtersChanged    = JSON.stringify(prevFilters) !== JSON.stringify(curFilters);
    const groupChanged      = prevProps.selectedGroupId !== this.props.selectedGroupId;
    const sectionChanged    = prevProps.selectedSection !== this.props.selectedSection;
    const ActionModeModeChanged   = prevProps.ActionMode !== this.props.ActionMode;
    const ActionTargetVariablesChanged   = (prevProps.ActionTargetVariables.TargetVariable1 !== this.props.ActionTargetVariables.TargetVariable1);
    const ActionTargetSubVariablesChanged   = prevProps.ActionTargetSubVariables !== this.props.ActionTargetSubVariables;
    const ActionTargetRangesChanged   = prevProps.ActionTargetRanges !== this.props.ActionTargetRanges;
    const ActionTargetConcentrationChanged = prevProps.ActionTargetConcentration !== this.props.ActionTargetConcentration;
  
    const levelChanged      = prevProps.geoLevel !== this.props.geoLevel;
    const stateChanged      = prevState.selectedStateCode !== this.state.selectedStateCode;
    const suppressIntermediateStateDrill =
      stateChanged &&
      !levelChanged &&
      this.props.geoLevel === "states" &&
      !!this.state.selectedStateCode;
  
    if (levelChanged || (stateChanged && !suppressIntermediateStateDrill)) {
      this.loadGeo(this.props.geoLevel, this.state.selectedStateCode, { doFit: false });
      this.fetchSecondaryMetrics(this.props.geoLevel, this.state.selectedStateCode)
    }
  
    const activeGroup = this.getActiveGroup();
    const activeSchema = (this.props.ActionMode === "filter" ? activeGroup?.TargetVariable1?.schema : activeGroup?.schema) || "public";
    const isTrackingActive = isTrackingSchema(activeSchema);
    const waveChanged = prevProps.waveSelect !== this.props.waveSelect;

    // A wave change can flip the CD boundary vintage (tracking → CD120 from
    // wave 4) without changing level/state, so it misses the reload above.
    // Reload the cd geometry only when the vintage actually changes.
    if (
      this.props.geoLevel === "cd" &&
      waveChanged &&
      !levelChanged &&
      !(stateChanged && !suppressIntermediateStateDrill) &&
      cdVersionFor(activeSchema, prevProps.waveSelect ?? prevProps.waveFilter) !==
        cdVersionFor(activeSchema, this.props.waveSelect ?? this.props.waveFilter)
    ) {
      this.loadGeo("cd", this.state.selectedStateCode, { doFit: false });
    }

    const compareChanged = prevProps.compareWaves !== this.props.compareWaves;
    const compareKeyChanged = prevProps.compareKey !== this.props.compareKey;
    const prefChanged  = prevProps.preferenceFilter !== this.props.preferenceFilter;
    const needRefetch =
      filtersChanged ||
      waveChanged ||
      levelChanged ||
      (stateChanged && !suppressIntermediateStateDrill) ||
      groupChanged ||
      sectionChanged ||
      prefChanged ||
      ActionModeModeChanged ||
      ActionTargetVariablesChanged ||
      compareChanged;
    if (needRefetch) {
      if (this.props.compareWaves) {
        this.fetchCompareMetrics(this.props.geoLevel, this.state.selectedStateCode);
        if (isTrackingActive) {
          this.fetchMetrics(this.props.geoLevel, this.state.selectedStateCode);
        }
      } else {
        this.fetchMetrics(this.props.geoLevel, this.state.selectedStateCode);
      }
      // Fast single-state filtered fetch to populate the state bar without waiting
      // for fetchStatesBaseline (all 50 states).
      if (this.props.geoLevel !== "states" && this.state.selectedStateCode) {
        this.fetchStateFiltered(this.state.selectedStateCode);
      }
    }

    if (levelChanged || (stateChanged && !suppressIntermediateStateDrill) || groupChanged || sectionChanged || ActionModeModeChanged || ActionTargetVariablesChanged || waveChanged || compareChanged) {
      this.fetchUnfilteredTotals(this.props.geoLevel, this.state.selectedStateCode);
    }
  
    // keep baseline in sync with filters/question/theme (no need to refetch on level/state change)
    if (filtersChanged || groupChanged || sectionChanged || ActionModeModeChanged || waveChanged || compareChanged) {
      this.fetchStatesBaseline();
    }

    if (groupChanged || sectionChanged || ActionModeModeChanged || waveChanged || compareChanged) {
      this.fetchStatesBaselineUnfiltered();
    }
  
    const rowsChanged  = prevState.metricRows !== this.state.metricRows;
    const baseChanged  = prevState.metricRowsStates !== this.state.metricRowsStates || prevState.metricRowsState !== this.state.metricRowsState;
    const modeChanged  = prevProps.mode !== this.props.mode;
    const heatDomainChanged = prevProps.heatDomain !== this.props.heatDomain;
    const heatColorChanged = prevProps.heatColor !== this.props.heatColor;
    const topStrengthShadingChanged = prevProps.topStrengthShading !== this.props.topStrengthShading;
    const geoChanged   = prevState.geoData !== this.state.geoData;
    const wavechange   = waveChanged;
    const selectedFeatureChanged = prevState.selectedFeature !== this.state.selectedFeature;
    const selectedStateCodeChanged = prevState.selectedStateCode !== this.state.selectedStateCode;

  
    const compareRowsChanged =
      prevState.compareRowsLatest !== this.state.compareRowsLatest ||
      prevState.compareRowsPrev !== this.state.compareRowsPrev;
    if (geoChanged || rowsChanged || compareRowsChanged || groupChanged || modeChanged || prefChanged || heatDomainChanged || heatColorChanged || topStrengthShadingChanged ||ActionModeModeChanged || ActionTargetVariablesChanged || ActionTargetSubVariablesChanged || ActionTargetRangesChanged || ActionTargetConcentrationChanged || wavechange || compareChanged || compareKeyChanged) {
      this.restyle();
    }

    const styledGeoChanged = prevState.styledGeo !== this.state.styledGeo;
    const styleJustLoaded = !prevState.styleLoaded && this.state.styleLoaded;
    if (styledGeoChanged || (styleJustLoaded && this.state.styledGeo)) this._applyFeatureState();
  
    if (rowsChanged || compareRowsChanged || baseChanged || levelChanged || stateChanged || filtersChanged || groupChanged || sectionChanged || prefChanged || ActionModeModeChanged|| ActionTargetVariablesChanged || ActionTargetSubVariablesChanged || ActionTargetRangesChanged || ActionTargetConcentrationChanged || selectedFeatureChanged || selectedStateCodeChanged) {
      const data = this.getAggregatesForSidebar();
      console.log("[sidebar] payload from MapPage", {
        waveFilter: this.props.waveFilter,
        nationalCount: data?.national?.length || 0,
        stateCount: data?.state?.length || 0,
        selectedCount: data?.selected?.length || 0,
      });
      this.props.onFeatureSelect?.({
        feature: this.state.selectedFeature,
        data,
        stateCode: this.state.selectedStateCode,
        stateName: this.state.selectedStateCode ? stateFipsToName[this.state.selectedStateCode] : null,
        selectedStateName: this.state.selectedStateCode ? stateFipsToName[this.state.selectedStateCode] : null,
        selectedAreaLabel: this.state.selectedFeature?.name || null,
        selectedFeatureLevel: this.state.selectedFeature?.level || null,
      });
    }

    if (this.props.isSidebarHover && !prevProps.isSidebarHover) {
      this.setState({ hoverInfo: null });
      this.props.handlehoverAreaChange?.(null);
    }
  
    if (prevProps.audienceInsightRequestId !== this.props.audienceInsightRequestId) {
      this.fetchTopDemographicInsight();
    }

    if (ActionTargetVariablesChanged || ActionTargetSubVariablesChanged || levelChanged || stateChanged || sectionChanged) {
      this.props.onAudienceInsightChange?.(null);
      this.props.onAudienceInsightLoadingChange?.(false);
    }


    if (
      this.props.selectedAreaIdToZoom &&
      this.props.selectedAreaIdToZoom !== prevProps.selectedAreaIdToZoom
    ) {
      this.zoomToAreaId(this.props.selectedAreaIdToZoom);
    }

    const chatHighlightChanged = prevProps.chatHighlightAreas !== this.props.chatHighlightAreas;
    if (chatHighlightChanged) {
      this._applyChatFeatureState(prevProps.chatHighlightAreas, this.props.chatHighlightAreas);
      if (this.props.chatHighlightAreas?.length > 0) {
        this.zoomToChatAreas(this.props.chatHighlightAreas);
      }
    }
    // Re-apply highlights when geoData loads (e.g. after a geo level change)
    if (
      !chatHighlightChanged &&
      this.props.chatHighlightAreas?.length > 0 &&
      this.state.geoData !== prevState.geoData
    ) {
      this._applyChatFeatureState([], this.props.chatHighlightAreas);
    }

    if (
      this.props.chatStateCode &&
      (this.props.chatStateCode !== prevProps.chatStateCode ||
        this.state.geoData !== prevState.geoData)
    ) {
      this.zoomToStateCode(this.props.chatStateCode);
    }

    // Context broadcasts — write-only, fields 8-10.
    // Standalone blocks; no interaction with the comparisons above.
    if (prevState.selectedStateCode !== this.state.selectedStateCode) {
      this.context.setSelectedStateCode(this.state.selectedStateCode);
    }
    const prevAreaName = prevState.selectedFeature?.name ?? null;
    const curAreaName  = this.state.selectedFeature?.name ?? null;
    if (prevAreaName !== curAreaName) {
      this.context.setSelectedAreaName(curAreaName);
    }
    const prevAreaLevel = prevState.selectedFeature?.level ?? null;
    const curAreaLevel  = this.state.selectedFeature?.level ?? null;
    if (prevAreaLevel !== curAreaLevel) {
      this.context.setSelectedAreaLevel(curAreaLevel);
    }
  }
  _geoCache = new Map();
  _geoCacheMaxSize = 10;
  _geoLoadIds = new Map();
  // In-flight geo loads, keyed exactly like _geoCache. The cache is only
  // written once a load FINISHES, and _geoLoadIds only discards a superseded
  // load's result after its fetch has fully completed, so without this every
  // concurrent loadGeo for the same key ran its own full download. On CD that
  // is 15.3MB a time, and a reel legitimately triggers several within the first
  // couple of seconds (the mount-time step nav, the readiness poll's re-nav,
  // the background preload, the blank-map recovery), which is why the network
  // panel showed the same .fgb fetched five times over.
  _geoInFlight = new Map();
  _inflightBaseline = null;
  _lastBaselineKey = null;
  _lastBaselineAt = 0;
  _inflightBaselineUnfiltered = null;
  _lastBaselineUnfilteredKey = null;
  _lastBaselineUnfilteredAt = 0;
  _inflightUnfiltered = null;
  _lastUnfilteredKey = null;
  _lastUnfilteredAt = 0;
  _stateFilteredInternals = { inflight: null, lastKey: null, lastAt: 0 };
  zoomToAreaId = (areaId) => {
    const fc = this.state.styledGeo || this.state.geoData;
    if (!fc?.features) return;
    const feat = fc.features.find(
      f => this.normalizeAreaIdForLevel(this.props.geoLevel, f.properties?.area_id) === this.normalizeAreaIdForLevel(this.props.geoLevel, areaId)
    );
    if (!feat) return;

    const level = this.props.geoLevel;

    if (level === "media_market") {
      this.fitBoundsFromFeatures([feat]);
      const marketLabel = feat.properties?.NAME || feat.properties?.name || String(areaId);
      const normalizedId = String(feat.properties?.area_id ?? areaId);
      this.setState(
        {
          selectedFeature: {
            level: "media_market",
            key: "area_id",
            value: normalizedId,
            name: marketLabel,
            rowMatchIds: [normalizedId],
          },
        },
        () => {
          const data = this.getAggregatesForSidebar();
          this.props.onFeatureSelect?.({
            feature: this.state.selectedFeature,
            data,
            selectedStateName: null,
            stateName: null,
            selectedAreaLabel: marketLabel,
            selectedFeatureLevel: "media_market",
          });
        }
      );
      return;
    }

    const value =
      level === "states"
        ? (feat.properties?.STATEFP || feat.properties?.GEOID || feat.properties?.area_id || areaId)
        : (feat.properties?.GEOID20 || feat.properties?.GEOID || feat.properties?.area_id || areaId);

    this.handleselectedFeatureChange?.({
      level,
      value: String(value),
      name: this.getFeatureDisplayName(level, feat.properties || {}) || String(areaId),
    });
  };
  
  fetchStatesBaseline = async () => {
    const group = this.getActiveGroup();
    if (!group || !group.keys?.length) {
      this.setState({ metricRowsStates: null });
      return;
    }

    const theme   = this.props.selectedSection || "pol";
    const filters = this.getFilters();
    const schema = (this.props.ActionMode === "filter" ? group?.TargetVariable1?.schema : group?.schema) || "public";
    const wave    = isTrackingSchema(schema) ? null : (this.props.waveSelect ?? this.props.waveFilter);
    const aggregationCol = (this.props.ActionMode === "filter" ? group?.TargetVariable1?.aggregation_column : group?.aggregation_column) || "total";
    const reqKey  = `states|*|${theme}|${schema}|${(group.keys || []).join(",")}|wave:${wave ?? "none"}|agg:${aggregationCol}|${JSON.stringify(filters)}`;const cached = this._getCache(`baseline:${reqKey}`);
    if (cached) {
      this.setState({ metricRowsStates: cached });
      return;
    }

    const now = Date.now();
    if (this._lastBaselineKey === reqKey && now - this._lastBaselineAt < 700) return;
    this._lastBaselineKey = reqKey; this._lastBaselineAt = now;

    if (this._inflightBaseline) this._inflightBaseline.abort();
    this._inflightBaseline = new AbortController();

    try {
      const res = await fetch(`/api/states_data_arrow/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stateCodes: [],           // all states
          theme,
          groupKeys: group.keys,
          schema,
          schema_type: schemaTypeFor(schema),
          weightCol: this.getWeightCol(schema),
           aggregation_column: aggregationCol|| "total",
          filters,                  // same demo filters as map
          ...(wave != null ? { wave } : {}),
        }),
        signal: this._inflightBaseline.signal,
      });
      if (!res.ok) throw new Error(`baseline fetch ${res.status}`);

      const buf   = await res.arrayBuffer();
      const table = tableFromIPC(buf);
      const rows  = rowsFromTable(table);  // one row per state (area_id is 2-digit FIPS)
      this.setState({ metricRowsStates: rows });
      this._setCache(`baseline:${reqKey}`, rows);
    } catch (e) {
      if (e.name !== "AbortError") console.error("baseline error:", e);
      this.setState({ metricRowsStates: null });
    } finally {
      this._inflightBaseline = null;
    }
  };

  fetchStatesBaselineUnfiltered = async () => {
    const group = this.getActiveGroup();
    const inFilter = this.props.ActionMode === "filter";
    const groupKeys = inFilter
      ? (group?.TargetVariable1?.keys || [])
      : (group?.keys || []);

    if (!groupKeys.length) {
      this.setState({ metricRowsStatesUnfiltered: null });
      return;
    }

    const theme   = this.props.selectedSection || "pol";
    const schema = (this.props.ActionMode === "filter" ? group?.TargetVariable1?.schema : group?.schema) || "public";
    const wave    = isTrackingSchema(schema) ? null : (this.props.waveSelect ?? this.props.waveFilter);
    const aggregationCol = (this.props.ActionMode === "filter" ? group?.TargetVariable1?.aggregation_column : group?.aggregation_column) || "total";
   
    const reqKey  = `states|*|${theme}|${schema}|${groupKeys.join(",")}|agg:${aggregationCol}|wave:${wave ?? "none"}|unfiltered`;
    const cached = this._getCache(`baseline-unfiltered:${reqKey}`);
    if (cached) {
      this.setState({ metricRowsStatesUnfiltered: cached });
      return;
    }

    const now = Date.now();
    if (this._lastBaselineUnfilteredKey === reqKey && now - this._lastBaselineUnfilteredAt < 700) return;
    this._lastBaselineUnfilteredKey = reqKey; this._lastBaselineUnfilteredAt = now;

    if (this._inflightBaselineUnfiltered) this._inflightBaselineUnfiltered.abort();
    this._inflightBaselineUnfiltered = new AbortController();

    try {
      const res = await fetch(`/api/states_data_arrow/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stateCodes: [],
          theme,
          groupKeys,
          schema,
          schema_type: schemaTypeFor(schema),
          weightCol: this.getWeightCol(schema),
    aggregation_column: aggregationCol || "total",
          filters: {}, // unfiltered
          ...(wave != null ? { wave } : {}),
        }),
        signal: this._inflightBaselineUnfiltered.signal,
      });
      if (!res.ok) throw new Error(`baseline unfiltered fetch ${res.status}`);

      const buf   = await res.arrayBuffer();
      const table = tableFromIPC(buf);
      const rows  = rowsFromTable(table);
      this.setState({ metricRowsStatesUnfiltered: rows });
      this._setCache(`baseline-unfiltered:${reqKey}`, rows);
    } catch (e) {
      if (e.name !== "AbortError") console.error("baseline unfiltered error:", e);
      this.setState({ metricRowsStatesUnfiltered: null });
    } finally {
      this._inflightBaselineUnfiltered = null;
    }
  };

  // Fast single-state filtered fetch — provides the "state" bar without waiting
  // for fetchStatesBaseline (all 50 states, slow).
  fetchStateFiltered = async (stateCode) => {
    if (!stateCode) return;
    const group = this.getActiveGroup();
    if (!group || !group.keys?.length) return;

    const theme   = this.props.selectedSection || "pol";
    const filters = this.getFilters();
    const schema  = (this.props.ActionMode === "filter" ? group?.TargetVariable1?.schema : group?.schema) || "public";
    const wave    = this.props.waveSelect ?? this.props.waveFilter;
    const aggregationCol = (this.props.ActionMode === "filter" ? group?.TargetVariable1?.aggregation_column : group?.aggregation_column) || "total";
    const reqKey  = `states|${stateCode}|${theme}|${schema}|${(group.keys || []).join(",")}|wave:${wave ?? "none"}|agg:${aggregationCol}|${JSON.stringify(filters)}`;

    const cached = this._getCache(`state-filtered:${reqKey}`);
    if (cached) {
      this.setState({ metricRowsState: cached });
      return;
    }

    const now = Date.now();
    if (this._stateFilteredInternals.lastKey === reqKey && now - this._stateFilteredInternals.lastAt < 700) return;
    this._stateFilteredInternals.lastKey = reqKey;
    this._stateFilteredInternals.lastAt = now;

    if (this._stateFilteredInternals.inflight) this._stateFilteredInternals.inflight.abort();
    this._stateFilteredInternals.inflight = new AbortController();

    try {
      const res = await fetch(`/api/states_data_arrow/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stateCodes: [stateCode],
          theme,
          groupKeys: group.keys,
          schema,
          schema_type: schemaTypeFor(schema),
          weightCol: this.getWeightCol(schema),
          aggregation_column: aggregationCol || "total",
          filters,
          ...(wave != null ? { wave } : {}),
        }),
        signal: this._stateFilteredInternals.inflight.signal,
      });
      if (!res.ok) throw new Error(`state-filtered fetch ${res.status}`);
      const buf  = await res.arrayBuffer();
      const rows = rowsFromTable(tableFromIPC(buf));
      this.setState({ metricRowsState: rows });
      this._setCache(`state-filtered:${reqKey}`, rows);
    } catch (e) {
      if (e.name !== "AbortError") console.error("state-filtered error:", e);
    } finally {
      this._stateFilteredInternals.inflight = null;
    }
  };

  fetchUnfilteredTotals = async (level, stateCode) => {
    const group = this.getActiveGroup();
    if (!group) {
      this.setState({ metricRowsUnfiltered: null });
      return;
    }
    const inFilter = this.props.ActionMode === "filter";
    const groupKeys = inFilter
      ? (group?.TargetVariable1?.keys || [])
      : (group?.keys || []);

    if (!groupKeys.length) {
      this.setState({ metricRowsUnfiltered: null });
      return;
    }

    const theme = this.props.selectedSection || "pol";
    const wave = this.props.waveSelect ?? this.props.waveFilter;
    const reqKey = `${level}|${stateCode || "*"}|${theme}|${(this.props.ActionMode === "filter" ? group?.TargetVariable1?.schema : group?.schema) || "public"}|${groupKeys.join(",")}|wave:${wave ?? "none"}|unfiltered`;
    const cached = this._getCache(`unfiltered:${reqKey}`);
    if (cached) {
      this.setState({ metricRowsUnfiltered: cached });
      return;
    }

    const now = Date.now();
    if (this._lastUnfilteredKey === reqKey && now - this._lastUnfilteredAt < 700) return;
    this._lastUnfilteredKey = reqKey; this._lastUnfilteredAt = now;

    if (this._inflightUnfiltered) this._inflightUnfiltered.abort();
    this._inflightUnfiltered = new AbortController();

    try {
      const res = await fetch(`/api/${level}_data_arrow/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stateCodes: stateCode ? [stateCode] : [],
          theme,
          groupKeys,
          schema: (this.props.ActionMode === "filter" ? group?.TargetVariable1?.schema : group?.schema) || "public",
          schema_type: schemaTypeFor((this.props.ActionMode === "filter" ? group?.TargetVariable1?.schema : group?.schema) || "public"),
          weightCol: this.getWeightCol((this.props.ActionMode === "filter" ? group?.TargetVariable1?.schema : group?.schema) || "public"),
    aggregation_column: (this.props.ActionMode === "filter" ? group?.TargetVariable1?.aggregation_column : group?.aggregation_column) || "total",
          filters: {}, // unfiltered
          wave,
        }),
        signal: this._inflightUnfiltered.signal,
      });
      if (!res.ok) throw new Error(`unfiltered fetch ${res.status}`);

      const buf = await res.arrayBuffer();
      const table = tableFromIPC(buf);
      const rows = rowsFromTable(table);
      this.setState({ metricRowsUnfiltered: rows });
      this._setCache(`unfiltered:${reqKey}`, rows);
    } catch (e) {
      if (e.name !== "AbortError") console.error("unfiltered error:", e);
      this.setState({ metricRowsUnfiltered: null });
    } finally {
      this._inflightUnfiltered = null;
    }
  };

  getNationalBenchmark = (group, subVar) => {
    const rows = Array.isArray(this.state.metricRowsStatesUnfiltered)
      ? this.state.metricRowsStatesUnfiltered
      : [];
    if (!rows.length) return null;
    const toArray = (v) => (Array.isArray(v) ? v : v != null ? [v] : []);
    const sumKeys = (keys, row) =>
      keys.reduce((acc, k) => acc + (Number(row?.[k]) || 0), 0);
    let num = 0;
    let den = 0;
    for (const r of rows) {
      let val;
      if (subVar === "net") {
        const posKeys = toArray(group?.TargetVariable1?.polarity?.pos);
        const negKeys = toArray(group?.TargetVariable1?.polarity?.neg);
        const pos = sumKeys(posKeys, r);
        const neg = sumKeys(negKeys, r);
        val = Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : null;
      } else {
        val = Number(r?.[subVar]);
      }
      const w = Number(r?.total_sum);
      if (Number.isFinite(val) && Number.isFinite(w)) {
        num += val * w;
        den += w;
      }
    }
    return den ? num / den : null;
  };

  getFilters = () => {
    if (this.props.filters && typeof this.props.filters === "object") {
      return this.props.filters;
    }
    const {
      genderFilter = null,
      ageFilter = null,
      ethnicityFilter = null,
      partyFilter = null,
      educationFilter = null,
      vote24Filter=null,
    } = this.props;
  
    return {
      gender: genderFilter ?? null,     
      age: ageFilter ?? null,            
      ethnicity: ethnicityFilter ?? null,
      party: partyFilter ?? null,        
      education: educationFilter ?? null, 
      vote24: vote24Filter ?? null
    };
  };

  _audienceInsightKey = null;
  _audienceInsightAbort = null;
  fetchTopDemographicInsight = async () => {
    // ── Guard clauses (wrapper-owned) ───────────────────────────────────────
    if (this.props.ActionMode !== "filter") return;
    if (!this.props.onAudienceInsightChange) return;
    const group = this.getActiveGroup?.();
    if (!group?.TargetVariable1?.keys?.length) return;
    const subVar = this.props.ActionTargetSubVariables?.TargetSubVariable1;
    if (!subVar) return;

    // ── Dedup check ──────────────────────────────────────────────────────────
    const stateCode = this.state.selectedStateCode;
    const selectedFeature = this.state.selectedFeature || null;
    const level = selectedFeature?.level || "states";
    const theme = this.props.selectedSection || "pol";
    const key = `${level}|${stateCode || "*"}|${selectedFeature?.value || "*"}|${theme}|${subVar}|${this.props.ActionTargetVariables?.TargetVariable1}`;
    if (this._audienceInsightKey === key) return;
    this._audienceInsightKey = key;

    // ── AbortController lifecycle ────────────────────────────────────────────
    if (this._audienceInsightAbort) this._audienceInsightAbort.abort();
    this._audienceInsightAbort = new AbortController();
    this.props.onAudienceInsightLoadingChange?.(true);

    // ── Config accessor resolution (entry-time snapshot, before any await) ───
    const schema = (group?.TargetVariable1?.schema || group?.schema || "public");
    const weightCol = this.getWeightCol(schema);
    const groupsById = this.getGroups();

    // ── Cache-aware transport adapter ────────────────────────────────────────
    // Key: JSON.stringify({url,body}) — different from original compound key,
    // SAFE because cache is result-irrelevant (cache hit == miss == same rows).
    const { signal } = this._audienceInsightAbort;
    const fetchRows = async (url, body, sig) => {
      const cacheKey = JSON.stringify({ url, body });
      const cached = this._getInsightCache(cacheKey);
      if (cached) return cached;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: sig,
      });
      if (!res.ok) {
        const e = new Error(`HTTP ${res.status}`);
        e.name = "SkipError";
        throw e;
      }
      if (sig?.aborted) throw new DOMException("Aborted", "AbortError");
      const buf = await res.arrayBuffer();
      const rows = _rowsFromTable(_tableFromIPC(buf));
      this._setInsightCache(cacheKey, rows);
      return rows;
    };

    // ── Core call + dispatch ─────────────────────────────────────────────────
    try {
      const result = await _computeAudienceInsight({
        selectedStateCode: stateCode,
        selectedFeature,
        subVar,
        theme,
        group,
        weightCol,
        groupsById,
        theme_colours: this.props.theme_colours,
        ActionMode: this.props.ActionMode,
        fetchRows,
        signal,
      });
      if (result === undefined) return; // abort-check early return — loading stays stuck (verbatim)
      if (result !== null) this.props.onAudienceInsightChange(result);
      this.props.onAudienceInsightLoadingChange?.(false);
    } catch (e) {
      if (e.name !== "AbortError") {
        console.error("audience insight error:", e);
      }
      this.props.onAudienceInsightLoadingChange?.(false);
    }
  };

  onStyleLoad = () => {
    this.setState({ styleLoaded: true });
    const map = this.mapRef?.current?.getMap?.();
    if (map) {
      map.on("styleimagemissing", (e) => {
        if (e?.id === "compare-arrow") {
          this._addCompareArrowImage(map);
        }
      });
      this._addCompareArrowImage(map);
      // Re-apply feature states after source data finishes loading (handles the
      // race where componentDidUpdate sets states before react-map-gl's useEffect
      // calls source.setData, which regenerates tiles and clears the states).
      map.on("sourcedata", (e) => {
        if (e.sourceId === "geo" && e.isSourceLoaded && this.state.styledGeo?.features?.length) {
          this._applyFeatureState();
        }
      });
    }
  };

  _addCompareArrowImage = (map) => {
    if (!map || map.hasImage?.("compare-arrow")) return;
    // Sleek elongated chevron arrow — SDF so Mapbox can recolour it at runtime.
    // Viewbox is tall/narrow so the arrow reads clearly even at small icon sizes.
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="32" viewBox="0 0 20 32">
        <path fill="white" d="M10 1 L19 14 L14 14 L14 31 L6 31 L6 14 L1 14 Z"/>
      </svg>
    `;
    const img = new Image(20, 32);
    img.onload = () => {
      if (!map.hasImage("compare-arrow")) {
        map.addImage("compare-arrow", img, { sdf: true });
      }
    };
    img.src = "data:image/svg+xml;utf8," + encodeURIComponent(svg);
  };

  getSelectedStateName = () => {
    const sc = String(this.state.selectedStateCode || "").padStart(2, "0");
    if (!sc || sc === "00") return null;
    return stateFipsToName[sc] || `State ${sc}`;
  };


  loadGeo = async (level, stateCode = null, { doFit = true, bounds = null, updateAreas = true, backgroundOnly = false } = {}) => {
    const { selectedSection } = this.props;
    const allowed = ALLOWED_LEVELS[selectedSection] || ALLOWED_LEVELS.default;
    if (!allowed.includes(level)) {
      console.warn(`${selectedSection} mode: overriding '${level}' → '${allowed[0]}'`);
      level = allowed[0];
    }

    // Congressional-district vintage (CD119 vs CD120) is picked automatically
    // from the active question's schema + selected wave (see utils/cdVersion).
    const cdGroup = this.getActiveGroup();
    const cdSchema = (this.props.ActionMode === "filter"
      ? cdGroup?.TargetVariable1?.schema
      : cdGroup?.schema) || "public";
    const cdWave = this.props.waveSelect ?? this.props.waveFilter;
    const cdVersion = cdVersionFor(cdSchema, cdWave);

    // --- set FlatGeobuf source URL ---
    let url = "";
    if (level === "states") url = process.env.PUBLIC_URL + "/usmaps/us_state_simplified.fgb";
    if (level === "counties") url = process.env.PUBLIC_URL + "/usmaps/us_county_2024_simplified.fgb";
    if (level === "cd") url = process.env.PUBLIC_URL + cdFgbPathFor(cdSchema, cdWave, { mobile: window.innerWidth < 600 });
    if (level === "zipcodes") {
      if (stateCode) {
        const padded = String(stateCode).padStart(2, "0");
        url = process.env.PUBLIC_URL + `/usmaps/zctas_by_state_fgb/${padded}.fgb`;
      } else {
        console.warn("ZIP level selected without stateCode — preventing full national ZIP load");
        return;
      }
    }
    if (level === "statesenate") url = process.env.PUBLIC_URL + "/usmaps/us_statelegsenate_simplified.fgb";
    if (level === "statehouse") url = process.env.PUBLIC_URL + "/usmaps/us_stateleghouse_simplified.fgb";
    if (level === "media_market") url = process.env.PUBLIC_URL + "/usmaps/us_dma.fgb";

    // Return immediately from cache when the same level+state was loaded before.
    // CD level also keys on the district vintage so ACC (CD120) and tracking
    // (CD119) never serve each other's cached polygons.
    // CD keys carry the vintage AND the mobile-variant marker so a window
    // resize across the 600px boundary never serves the wrong detail level.
    const geoKey = `${level}:${stateCode || ""}${level === "cd" ? `:${cdVersion}${window.innerWidth < 600 ? ":m" : ""}` : ""}`;
    // Foreground-load sequence: only the LATEST foreground loadGeo may write
    // geoData. Per-level _geoLoadIds can't stop a slow "states" load from
    // clobbering a newer "cd" load (all-white joins); this global ordering can.
    const fgSeq = backgroundOnly ? null : (this._fgGeoSeq = (this._fgGeoSeq || 0) + 1);
    const cachedGeo = this._geoCache.get(geoKey);
    if (cachedGeo) {
      this.setState(prev => ({ geoPreloadStatus: { ...prev.geoPreloadStatus, [level]: "loaded" } }));
      if (backgroundOnly) return;
      if (updateAreas) this.props.handlePossibleAreasChange(cachedGeo.areas);
      this.setState({ geoData: cachedGeo.geo, styledGeo: null }, () => {
        if (!doFit) return;
        const map = this.mapRef.current?.getMap();
        if (bounds) { map?.fitBounds(bounds, { padding: 24, duration: 500 }); return; }
        if (!stateCode) {
          map?.fitBounds([[-125, 24], [-66, 49]], { padding: 20, duration: 500 });
        } else {
          this.fitToGeo(cachedGeo.geo);
        }
      });
      return;
    }

    // Use full US bounds so every geography switch loads all features,
    // not just whatever happens to be in the current viewport.
    const rect = { minX: -180, minY: 17, maxX: -60, maxY: 72 };

    // Guard against superseded loads for the same level (e.g. rapid level
    // switching). Only a FOREGROUND load takes a new id.
    //
    // A background preload used to bump this counter too, and that was the
    // blank-map bug. Sequence, which is the normal one in a reel because the
    // step nav fires at ~160ms and geoPreloadOrder fires at 1500ms:
    //   1. foreground loadGeo("cd") takes id 1 and starts streaming
    //   2. background preload of the same level takes id 2
    //   3. the foreground load finishes, sees the id is now 2, and returns
    //      WITHOUT writing geoData
    //   4. the preload finishes and fills the cache, but it is backgroundOnly
    //      so it never writes geoData either
    // Net result: basemap with no polygons, which is what the reels carry a
    // recovery hack for. A preload now inherits the current id, so it still
    // stands down if a genuine foreground load overtakes it, but it can no
    // longer knock one out.
    let loadId;
    if (backgroundOnly) {
      loadId = this._geoLoadIds.get(level) || 0;
    } else {
      loadId = (this._geoLoadIds.get(level) || 0) + 1;
      this._geoLoadIds.set(level, loadId);
    }

    try {
      // Share one fetch-and-process per geoKey. Callers that arrive while a
      // load is already streaming await the same promise instead of starting a
      // second download. Everything AFTER this point is unchanged and still
      // runs per caller, so each one keeps its own supersede checks, its own
      // backgroundOnly handling and its own setState.
      let inFlight = this._geoInFlight.get(geoKey);
      if (!inFlight) {
        inFlight = (async () => {
          const loaded = [];
          for await (const feature of fgb.deserialize(url, rect, undefined, true)) {
            loaded.push(feature);
          }
          // processGeoFeatures is pure, so the processed result is shared too
          // rather than every follower re-doing the same work on one array.
          return _processGeoFeatures(loaded, level, stateCode);
        })();
        this._geoInFlight.set(geoKey, inFlight);
        // Clear on failure as well as success. A rejected promise left in the
        // map would make every later attempt await the same rejection, turning
        // one dropped connection into a permanently broken level.
        inFlight.catch(() => {}).then(() => {
          if (this._geoInFlight.get(geoKey) === inFlight) this._geoInFlight.delete(geoKey);
        });
      }
      const processed = await inFlight;
      // Normalised with `|| 0` on both sides. A background preload that is the
      // FIRST load of a level inherits id 0 from an unset key, and a bare
      // get() would return undefined here, so `undefined !== 0` would make it
      // stand down and never fill the cache. Foreground ids start at 1, so the
      // coalesce is a no-op for them.
      if ((this._geoLoadIds.get(level) || 0) !== loadId) return;
      // A newer foreground load (any level) started while this one streamed:
      // let it win. Background preloads only fill the cache, so they pass.
      if (!backgroundOnly && this._fgGeoSeq !== fgSeq) {
        if (this._geoCache.size >= this._geoCacheMaxSize) {
          this._geoCache.delete(this._geoCache.keys().next().value);
        }
        const { geo: staleGeo, possibleAreas: staleAreas } = processed;
        this._geoCache.set(geoKey, { geo: staleGeo, areas: staleAreas });
        this.setState(prev => ({ geoPreloadStatus: { ...prev.geoPreloadStatus, [level]: "loaded" } }));
        return;
      }

      // ── Pure computation: P3–P7 extracted to geoUtils.processGeoFeatures ──
      // Already computed inside the shared in-flight promise above.
      const { geo, possibleAreas: PossibleAreas } = processed;

      if (updateAreas) {
        this.props.handlePossibleAreasChange(PossibleAreas);
      }

      // Cache the processed geo so repeat visits to the same level are instant
      if (this._geoCache.size >= this._geoCacheMaxSize) {
        this._geoCache.delete(this._geoCache.keys().next().value);
      }
      this._geoCache.set(geoKey, { geo, areas: PossibleAreas });

      this.setState(prev => ({ geoPreloadStatus: { ...prev.geoPreloadStatus, [level]: "loaded" } }));

      if (backgroundOnly) return;

      this.setState({ geoData: geo, styledGeo: null }, () => {
        if (!doFit) return;

        const map = this.mapRef.current?.getMap();

        if (bounds) {
          map?.fitBounds(bounds, { padding: 24, duration: 500 });
          return;
        }

        if (!stateCode) {
          map?.fitBounds([[-125, 24], [-66, 49]], { padding: 20, duration: 500 });
        } else {
          this.fitToGeo(geo);
        }
      });
    } catch (e) {
      console.error("shape load failed:", e);
    }
  };
  
  

  onMapClick = (evt) => {
    const feature = evt.features?.[0];
    if (!feature) return;
  
    console.log('Example area_id from map:', feature.properties.area_id);
    console.log('Example area_ids from backend:', Object.keys(this.state.metricRows?.[0] ? 
      Object.fromEntries(this.state.metricRows.map(r => [r.area_id, r])) : {}).slice(0, 5));
    
    const level = this.props.geoLevel;
    const p = feature.properties || {};
    const areaId = String(p.area_id ?? "");
  
    // Local helper: robustly compute bounds for one or many features (Polygon/MultiPolygon safe)
    const safeBoundsFromFeatures = (feats) => {
      if (!this.mapRef.current || !Array.isArray(feats) || feats.length === 0) return null;
  
      const bounds = new mapboxgl.LngLatBounds();
      const pushCoords = (coords, depth = 0) => {
        if (!coords) return;
        if (typeof coords[0] === "number" && typeof coords[1] === "number") {
          // it's a coordinate pair
          bounds.extend(coords);
        } else if (Array.isArray(coords)) {
          coords.forEach((c) => pushCoords(c, depth + 1));
        }
      };
  
      feats.forEach((f) => pushCoords(f.geometry?.coordinates));
      return bounds;
    };
  
    const map = this.mapRef.current?.getMap();
  
    const callAllMetrics = (lvl, scOrNull) => {
      const nextGroup = this.getActiveGroup();
      const nextSchema = (this.props.ActionMode === "filter" ? nextGroup?.TargetVariable1?.schema : nextGroup?.schema) || "public";
      if (this.props.compareWaves) {
        this.fetchCompareMetrics(lvl, scOrNull);
        if (isTrackingSchema(nextSchema)) {
          this.fetchMetrics(lvl, scOrNull);
        }
      } else {
        this.fetchMetrics(lvl, scOrNull);
      }
      if (typeof this.fetchSecondaryMetrics === "function") {
        this.fetchSecondaryMetrics(lvl, scOrNull);
      }
    };
  
    const clickedLevel = level;
  
  
    if (
      areaId &&
      this.state.selectedFeature &&
      this.state.selectedFeature.level === clickedLevel &&
      this.state.selectedFeature.value === areaId
    ) {
   
      if (
        clickedLevel === "counties" ||
        clickedLevel === "cd" ||
        clickedLevel === "statesenate" ||
        clickedLevel === "statehouse" ||
        clickedLevel === "zipcodes"
      ) {
        if (this.state.selectedStateBounds) {
          map?.fitBounds(this.state.selectedStateBounds, { padding: 28, duration: 500 });
        }

        const stateCode = this.state.selectedStateCode
          ? String(this.state.selectedStateCode).padStart(2, "0")
          : String(p.STATEFP || "").padStart(2, "0");
        const stateLabel = stateFipsToName[stateCode] || `State ${stateCode}`;

        this.setState(
          {
            selectedFeature: stateCode
              ? {
                  level: "states",
                  key: "STATEFP",
                  value: stateCode,
                  name: stateLabel,
                  rowMatchIds: [stateCode],
                }
              : null,
          },
          () => {
            const data = this.getAggregatesForSidebar();
            const selectedStateName = this.getSelectedStateName();
            this.props.onFeatureSelect?.({
              feature: this.state.selectedFeature,
              data,
              selectedStateName,
              stateName: selectedStateName,
              selectedAreaLabel: this.state.selectedFeature?.name || null,
              selectedFeatureLevel: this.state.selectedFeature?.level || null,
            });
          }
        );
        return;
      }
  
      if (clickedLevel === "states") {
        this.setState({ selectedFeature: null, selectedStateCode: null }, () => {
          map?.fitBounds([[-125, 24], [-66, 49]], { padding: 20, duration: 500 });
          this.props.onFeatureSelect?.({
            feature: null,
            data: this.getAggregatesForSidebar(),
            selectedStateName: null,
            stateName: null,
            selectedAreaLabel: null,
            selectedFeatureLevel: null,
          });
        });
        return;
      }

      if (clickedLevel === "media_market") {
        this.setState({ selectedFeature: null }, () => {
          map?.fitBounds([[-125, 24], [-66, 49]], { padding: 20, duration: 500 });
          this.props.onFeatureSelect?.({
            feature: null,
            data: this.getAggregatesForSidebar(),
            selectedStateName: null,
            stateName: null,
            selectedAreaLabel: null,
            selectedFeatureLevel: null,
          });
        });
        return;
      }
    }
  

    if (level === "states") {
      const stateCode = String(p.STATEFP || "").padStart(2, "0");
      if (!stateCode) return;
  
      const stateLabel = stateFipsToName[stateCode] || p.NAME || `State ${stateCode}`;
      // zoom to that state polygon in the states layer
      const stateBounds = safeBoundsFromFeatures([feature]);
      map?.fitBounds(stateBounds, { padding: 28, duration: 500 });
  
      this.setState(
        {
          selectedStateCode: stateCode,
          selectedStateBounds: stateBounds,
          selectedFeature: {
            level: "states",
            key: "STATEFP",
            value: stateCode,
            name: stateLabel,
            rowMatchIds: [stateCode],
          },
        },
        () => {
          const { selectedSection } = this.props;

          const THEME_NEXT_LEVEL = {
            phrma: "statesenate",
            stateleg: "statesenate",
            example_client: "cd",
          };
          
          const nextLevel = THEME_NEXT_LEVEL[selectedSection] || "cd";
          this.props.handlegeoLevelChange(nextLevel);
  
          const data = this.getAggregatesForSidebar();
          const selectedStateName = this.getSelectedStateName();
          this.props.onFeatureSelect?.({ feature: null, data, selectedStateName, stateName: selectedStateName });
        }
      );
      return;
    }
  
 
    if (level === "media_market") {
      const marketBounds = safeBoundsFromFeatures([feature]);
      if (marketBounds) map?.fitBounds(marketBounds, { padding: 32, duration: 500 });
      const marketLabel = p.NAME || p.name || areaId;
      this.setState(
        {
          selectedFeature: {
            level: "media_market",
            key: "area_id",
            value: areaId,
            name: marketLabel,
            rowMatchIds: [areaId],
          },
        },
        () => {
          const data = this.getAggregatesForSidebar();
          this.props.onFeatureSelect?.({
            feature: this.state.selectedFeature,
            data,
            selectedStateName: null,
            stateName: null,
            selectedAreaLabel: marketLabel,
            selectedFeatureLevel: "media_market",
          });
        }
      );
      return;
    }

    if (
      (level === "cd" ||
       level === "counties" ||
       level === "zipcodes" ||
       level === "statesenate" ||
       level === "statehouse") &&
      !this.state.selectedStateCode
    ) {
      const stateCode = String(p.STATEFP || "").padStart(2, "0");
      if (stateCode) {
        // find all current-geo features for that state & fit to ALL of them → approximates state bounds
        const featsForState = (this.state.geoData?.features || []).filter(
          (f) => String(f.properties?.STATEFP).padStart(2, "0") === stateCode
        );
  
        if (featsForState.length) {
          const stateBounds = safeBoundsFromFeatures(featsForState);
          // set state context (but don't select the sub-feature)
          this.setState(
            {
              selectedStateCode: stateCode,
              selectedStateBounds: stateBounds,
              selectedFeature: {
                level: "states",
                key: "STATEFP",
                value: stateCode,
                name: stateFipsToName[stateCode] || `State ${stateCode}`,
                rowMatchIds: [stateCode],
              },
            },
            () => {
              map?.fitBounds(stateBounds, { padding: 28, duration: 600 });
  
              // (Optional) Update sidebar with state context
              const data = this.getAggregatesForSidebar();
              const selectedStateName = this.getSelectedStateName();
              this.props.onFeatureSelect?.({
                feature: null,
                data,
                selectedStateName,
                stateName: selectedStateName,
                selectedAreaLabel: null,
                selectedFeatureLevel: "states",
              });
            }
          );
          return; // STOP — do not zoom to the CD/county/zip itself
        }
      }
      // If we couldn't compute state bounds for some reason, fall back to feature zoom:
      // (you can remove this fallback if you never want it)
      const fb = safeBoundsFromFeatures([feature]);
      fb && map?.fitBounds(fb, { padding: 24, duration: 450 });
      return;
    }
  
 
    if (level === "cd" || level === "counties" || level === "zipcodes") {
      const fb = safeBoundsFromFeatures([feature]);
      fb && map?.fitBounds(fb, { padding: 24, duration: 450 });
    }
  
    let label;
    if (clickedLevel === "zipcodes") {
      label = `${feature.properties?.GEOID20 ?? areaId} — ${feature.properties?.NAME ?? ""}`.trim();
    } else {
      label = feature.properties?.NAME || feature.properties?.name || areaId;
    }
  
    const sc = String(feature.properties?.STATEFP || "").padStart(2, "0");
    const cd2 = String(
      feature.properties?.CD119FP ??
        feature.properties?.CD120FP ??
        feature.properties?.CD118FP ??
        feature.properties?.CD116FP ??
        ""
    ).padStart(2, "0");
    const cnty = String(feature.properties?.COUNTYFP ?? "").padStart(3, "0");
  
    let rowMatchIds = [areaId];
    if (clickedLevel === "cd" && sc && cd2) {
      rowMatchIds.push(sc + cd2, `${sc}-${cd2}`);
    }
    if (clickedLevel === "counties" && sc && cnty) {
      rowMatchIds.push(sc + cnty); // 5-digit FIPS
    }
  
    this.setState(
      {
        selectedFeature: {
          level: clickedLevel,
          key: "area_id",
          value: areaId,
          name: label,
          rowMatchIds,
        },
      },
      () => {
        const data = this.getAggregatesForSidebar();
        const selectedStateName = this.getSelectedStateName();
        const selectedAreaLabel = this.state.selectedFeature?.name || null;
        const selectedFeatureLevel = this.state.selectedFeature?.level || null;
  
        this.props.onFeatureSelect?.({
          feature: this.state.selectedFeature,
          data,
          selectedStateName,
          stateName: selectedStateName,
          selectedAreaLabel,
          selectedFeatureLevel,
        });
      }
    );
  };
  
  
  handleGeoLevelChange = (newLevel) => {
    console.log("Geo level change triggered:", newLevel);
    if (!newLevel) return;
  
    const prevLevel = this.props.geoLevel;
    const { selectedSection } = this.props; // e.g., "phrma", "econ", etc.
  
    // --- restrict allowed levels by theme ---
    const allowed = ALLOWED_LEVELS[selectedSection] || ALLOWED_LEVELS.default;
    if (!allowed.includes(newLevel)) {
      console.warn(`${selectedSection} mode: overriding '${newLevel}' → '${allowed[0]}'`);
      newLevel = allowed[0];
    }
    // --------------------------------------------
  
    this.setState((prev) => {
      let nextFeature = prev.selectedFeature;
  
      // When leaving counties/cd/zip, fall back to the state if available
      if (
        (prevLevel === "counties" || prevLevel === "cd" || prevLevel === "zipcodes") &&
        newLevel !== prevLevel
      ) {
        if (prev.selectedStateCode) {
          const stateLabel =
            stateFipsToName[prev.selectedStateCode] ||
            `State ${prev.selectedStateCode}`;
          nextFeature = {
            level: "states",
            key: "STATEFP",
            value: prev.selectedStateCode,
            name: stateLabel,
            rowMatchIds: [prev.selectedStateCode],
          };
        } else {
          nextFeature = null;
        }
      }
  
      return {
        styledGeo: null,
        selectedStateCode: prev.selectedStateCode,
        selectedFeature: nextFeature,
      };
    }, () => {
      const sc = this.state.selectedStateCode;
      const map = this.mapRef.current?.getMap();
      this.props.handlegeoLevelChange(newLevel);
  
      // ==== Always call metrics functions ====
      const callAllMetrics = (level, stateCodeOrNull) => {
        this.fetchMetrics(level, stateCodeOrNull);
        if (typeof this.fetchSecondaryMetrics === "function") {
          this.fetchSecondaryMetrics(level, stateCodeOrNull);
        }
      };
      // ========================================
  
      // --- phrma LOGIC: restrict navigation levels dynamically ---
      const isphrmaMode = selectedSection === "phrma";
      if (isphrmaMode && !allowed.includes(newLevel)) {
        console.log(`phrma mode: redirecting to ${allowed[0]}`);
        newLevel = allowed[0];
      }
      // ------------------------------------------------------------
  
      if (newLevel !== "states") {
        // metrics + geo at the new granularity
        callAllMetrics(newLevel, sc || null);
        this.loadGeo(newLevel, sc || null, { doFit: false });
  
        // Transition-based state-fit logic
        if (
          (prevLevel === "cd" || prevLevel === "zipcodes") &&
          newLevel === "counties" &&
          this.state.selectedStateBounds
        ) {
          map?.fitBounds(this.state.selectedStateBounds, { padding: 28, duration: 450 });
        }
  
        if (
          prevLevel === "counties" &&
          (newLevel === "cd" || newLevel === "zipcodes") &&
          this.state.selectedStateBounds
        ) {
          map?.fitBounds(this.state.selectedStateBounds, { padding: 28, duration: 450 });
        }
  
        if (!sc || !this.state.selectedStateBounds) {
          map?.fitBounds(MAINLAND_BOUNDS, { padding: 20, duration: 500 });
        }
      } else {
        // newLevel === "states"
        callAllMetrics("states", null);
        this.loadGeo("states", null, { doFit: false });
  
        if (sc) {
          const stateLabel = stateFipsToName[sc] || `State ${sc}`;
          this.setState({
            selectedFeature: {
              level: "states",
              key: "STATEFP",
              value: sc,
              name: stateLabel,
              rowMatchIds: [sc],
            },
          });
  
          if (this.state.selectedStateBounds) {
            map?.fitBounds(this.state.selectedStateBounds, { padding: 28, duration: 500 });
          } else {
            map?.fitBounds(MAINLAND_BOUNDS, { padding: 20, duration: 500 });
          }
        } else {
          this.setState({ selectedFeature: null });
          map?.fitBounds(MAINLAND_BOUNDS, { padding: 20, duration: 500 });
        }
      }
  
      const data = this.getAggregatesForSidebar();
      this.props.onFeatureSelect?.({ feature: this.state.selectedFeature, data });
    });
  };
  
    
  fitToGeo = (geo) => {
    if (!this.mapRef.current || !geo?.features?.length) return;
    const coords = geo.features.flatMap(f =>
      f.geometry.type === "Polygon" ? f.geometry.coordinates.flat(2) : f.geometry.coordinates.flat(3)
    );
    if (!coords.length) return;
    const lons = coords.filter((_, i) => i % 2 === 0);
    const lats = coords.filter((_, i) => i % 2 === 1);
    const minLng = Math.min(...lons), maxLng = Math.max(...lons);
    const minLat = Math.min(...lats), maxLat = Math.max(...lats);
    this.mapRef.current.getMap().fitBounds([[minLng, minLat],[maxLng, maxLat]], { padding: 24 });
  };

  requestKey = (level, stateCode) => {
    const group = this.getActiveGroup();
    const theme = this.props.selectedSection || "pol";
    const filters = this.getFilters(); // <-- key change
    const schema = (this.props.ActionMode === "filter" ? group?.TargetVariable1?.schema : group?.schema) || "public";
    const wave = isTrackingSchema(schema) ? null : (this.props.waveSelect ?? this.props.waveFilter);
    let groupKeys
     if(this.props.ActionMode==="explore"){
      groupKeys = (group?.keys || []).join(",");
     } else if(this.props.ActionMode==="filter"){
      groupKeys = [
                    ...(group?.TargetVariable1?.keys || [])
                  ].join(",");
     }
    const zipPrefSuffix =
      level === "zipcodes" && this.props.ActionMode === "explore"
        ? `|pref:${this.props.preferenceFilter ?? "none"}`
        : "";
    return `${level}|${stateCode || "*" }|${theme}|${schema}|${groupKeys}|wave:${wave ?? "none"}|${JSON.stringify(filters)}${zipPrefSuffix}`;
  };

  getWeightCol = (schema) => {
    const themeConfig = THEMES?.[this.props.selectedSection || "pol"];
    return themeConfig?.weight || "total";
  };

  _getCache = (key) => {
    const entry = this._cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.ts > this._cacheTtlMs) {
      this._cache.delete(key);
      return null;
    }
    return entry.data;
  };

  _setCache = (key, data) => {
    this._cache.set(key, { ts: Date.now(), data });
    if (this._cache.size > this._cacheMax) {
      const oldestKey = this._cache.keys().next().value;
      if (oldestKey) this._cache.delete(oldestKey);
    }
  };

  _getInsightCache = (key) => {
    const entry = this._insightCache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.ts > this._insightCacheTtlMs) {
      this._insightCache.delete(key);
      return null;
    }
    return entry.data;
  };

  _setInsightCache = (key, data) => {
    this._insightCache.set(key, { ts: Date.now(), data });
  };

  getLatestTwoWaves = (rows) => {
    const fromOptions = (this.props.waveOptions || [])
      .map((w) => Number(w))
      .filter((w) => Number.isFinite(w))
      .sort((a, b) => b - a);
    if (fromOptions.length >= 2) return [fromOptions[0], fromOptions[1]];
    const fromRows = (rows || [])
      .map((r) => Number(r?.wave))
      .filter((w) => Number.isFinite(w))
      .sort((a, b) => b - a);
    if (fromRows.length >= 2) return [fromRows[0], fromRows[1]];
    return null;
  };

  fetchCompareMetrics = async (level, stateCode) => {
    const group = this.getActiveGroup();
    if (!group || (!group.keys?.length && this.props.ActionMode === "explore") || (!group?.TargetVariable1?.keys?.length && this.props.ActionMode === "filter")) {
      this.setState({ compareRowsLatest: null, compareRowsPrev: null, compareMetricRows: null, compareWavePair: null });
      return;
    }
    if (level === "zipcodes" && !stateCode) {
      this.setState({ compareRowsLatest: null, compareRowsPrev: null, compareMetricRows: null, compareWavePair: null, loading: false });
      return;
    }

    const waves = this.getLatestTwoWaves(this.state.metricRows);
    if (!waves) {
      this.setState({ compareRowsLatest: null, compareRowsPrev: null, compareMetricRows: null, compareWavePair: null });
      return;
    }
    const [latestWave, prevWave] = waves;

    const theme = this.props.selectedSection || "pol";
    const filters = this.getFilters();

    const normalizeKeys = (v) => {
      if (Array.isArray(v)) return v;
      if (typeof v === "string" && v.length) return [v];
      return [];
    };
    let keysBody;
    if (this.props.ActionMode === "explore") {
      keysBody = normalizeKeys(group?.keys);
    } else if (this.props.ActionMode === "filter") {
      keysBody = normalizeKeys(group?.TargetVariable1?.keys);
    }

    if (level === "zipcodes" && this.props.ActionMode === "explore") {
      const groupIsLikert = group?.type === "likert" && group?.polarity?.pos && group?.polarity?.neg;
      if (groupIsLikert && (!this.props.preferenceFilter || this.props.preferenceFilter === "net")) {
        const pos = normalizeKeys(group?.polarity?.pos);
        const neg = normalizeKeys(group?.polarity?.neg);
        keysBody = Array.from(new Set([...pos, ...neg]));
      } else if (this.props.preferenceFilter && group?.keys?.includes(this.props.preferenceFilter)) {
        keysBody = [this.props.preferenceFilter];
      } else if (group?.keys?.length) {
        keysBody = [normalizeKeys(group?.keys)[0]];
      }
    }

    const baseBody = {
      stateCodes: stateCode ? [stateCode] : [],
      theme,
      groupKeys: keysBody,
      schema: (this.props.ActionMode === "filter" ? group?.TargetVariable1?.schema : group?.schema) || "public",
      schema_type: schemaTypeFor((this.props.ActionMode === "filter" ? group?.TargetVariable1?.schema : group?.schema) || "public"),
      weightCol: this.getWeightCol((this.props.ActionMode === "filter" ? group?.TargetVariable1?.schema : group?.schema) || "public"),
    aggregation_column: (this.props.ActionMode === "filter" ? group?.TargetVariable1?.aggregation_column : group?.aggregation_column) || "total",
      filters,
    };

    this.setState({ loading: true });
    try {
      const [resLatest, resPrev] = await Promise.all(
        [latestWave, prevWave].map((w) =>
          fetch(`/api/${level}_data_arrow/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...baseBody, wave: w }),
          })
        )
      );
      if (!resLatest.ok || !resPrev.ok) throw new Error("compare fetch failed");
      const bufLatest = await resLatest.arrayBuffer();
      const bufPrev = await resPrev.arrayBuffer();
      const rowsLatest = rowsFromTable(tableFromIPC(bufLatest));
      const rowsPrev = rowsFromTable(tableFromIPC(bufPrev));
      const keys = this.props.ActionMode === "filter"
        ? (group?.TargetVariable1?.keys || [])
        : (group?.keys || []);
      const normalize = (id) => this.normalizeAreaIdForLevel(level, id);
      const latestById = new Map(rowsLatest.map((r) => [normalize(r?.area_id ?? ""), r]));
      const prevById = new Map(rowsPrev.map((r) => [normalize(r?.area_id ?? ""), r]));
      const ids = new Set([...latestById.keys(), ...prevById.keys()]);
      const deltaRows = Array.from(ids).map((id) => {
        const a = latestById.get(id) || {};
        const b = prevById.get(id) || {};
        // Use the raw area_id from the API row, not the normalised key.
        // restyle() builds its byId map from raw area_ids (no normalisation),
        // so delta rows must carry the original ID to match geo features.
        // We only normalise above to align the two wave fetches with each other.
        const rawAreaId = a?.area_id ?? b?.area_id ?? id;
        const row = {
          area_id: rawAreaId,
          total_sum: a.total_sum ?? b.total_sum ?? null,
          wave_latest: latestWave,
          wave_prev: prevWave,
        };
        keys.forEach((k) => {
          const av = Number(a?.[k]);
          const bv = Number(b?.[k]);
          row[k] = (Number.isFinite(av) ? av : 0) - (Number.isFinite(bv) ? bv : 0);
        });
        return row;
      });
      this.setState({
        compareRowsLatest: rowsLatest,
        compareRowsPrev: rowsPrev,
        compareMetricRows: deltaRows,
        compareWavePair: [latestWave, prevWave],
        loading: false,
      });
      console.log("[compare] fetched", {
        level,
        latestWave,
        prevWave,
        latestRows: rowsLatest.length,
        prevRows: rowsPrev.length,
        deltaRows: deltaRows.length,
      });
    } catch (e) {
      console.error("compare metrics error:", e);
      this.setState({ compareRowsLatest: null, compareRowsPrev: null, compareMetricRows: null, compareWavePair: null, loading: false });
    }
  };

  fetchMetrics = async (level, stateCode) => {
  const group = this.getActiveGroup();

  if (!group || (!group.keys?.length && this.props.ActionMode==="explore") || (!group?.TargetVariable1?.keys?.length && this.props.ActionMode==="filter")) {
    this.setState({ metricRows: null });
    return;
  }
  if (level === "zipcodes" && !stateCode) {
    this.setState({ metricRows: null, loading: false });
    return;
  }

  const theme = this.props.selectedSection || "pol";
  const filters = this.getFilters();
  const reqKey = this.requestKey(level, stateCode);
  // Empty arrays are never cached (and never honored from cache): the backend
  // swallows load errors into empty-but-200 responses, and serving those from
  // cache froze an all-white map for the TTL. Treat empty as a miss so the
  // next attempt re-asks.
  const cached = this._getCache(`metrics:${reqKey}`);
  if (cached && cached.length) {
    this.setState({ metricRows: cached, loading: false });
    return;
  }

  const now = Date.now();
  if (this._lastKey === reqKey && now - this._lastAt < 700) return;
  this._lastKey = reqKey;
  this._lastAt = now;

  if (this._inflight) this._inflight.abort();
  // Captured locally so this request's catch/finally can tell whether it still
  // owns the shared slots (an aborting successor takes them over).
  const ctrl = new AbortController();
  this._inflight = ctrl;

  const normalizeKeys = (v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === "string" && v.length) return [v];
    return [];
  };
  let keysBody
    if(this.props.ActionMode==="explore"){
    keysBody = normalizeKeys(group?.keys);
    } else if(this.props.ActionMode==="filter"){
    keysBody = normalizeKeys(group?.TargetVariable1?.keys);
    }

  if (level === "zipcodes" && this.props.ActionMode === "explore") {
    const groupIsLikert = group?.type === "likert" && group?.polarity?.pos && group?.polarity?.neg;
    if (groupIsLikert && (!this.props.preferenceFilter || this.props.preferenceFilter === "net")) {
      const pos = normalizeKeys(group?.polarity?.pos);
      const neg = normalizeKeys(group?.polarity?.neg);
      keysBody = Array.from(new Set([...pos, ...neg]));
    } else if (this.props.preferenceFilter && group?.keys?.includes(this.props.preferenceFilter)) {
      keysBody = [this.props.preferenceFilter];
    } else if (group?.keys?.length) {
  keysBody = normalizeKeys(group?.keys);
}
  }

  const activeSchema = (this.props.ActionMode === "filter" ? group?.TargetVariable1?.schema : group?.schema) || "public";
  const selectedWave = isTrackingSchema(activeSchema) ? null : (this.props.waveSelect ?? this.props.waveFilter);
  const body = {
    stateCodes: stateCode ? [stateCode] : [],
    theme,
    groupKeys: keysBody,
    schema: activeSchema,
    schema_type: schemaTypeFor(activeSchema),
    weightCol: this.getWeightCol(activeSchema),
    filters,
    aggregation_column: (this.props.ActionMode === "filter" ? group?.TargetVariable1?.aggregation_column : group?.aggregation_column) || "total",
    ...(selectedWave != null ? { wave: selectedWave } : {}),
  };

  console.log("[metrics] request", {
    level,
    stateCode,
    selectedVariable: this.props.selectedGroupId,
    actionMode: this.props.ActionMode,
    preferenceFilter: this.props.preferenceFilter,
    keysBody,
    schema: body.schema,
    aggregation_column: body.aggregation_column
  });

  this.setState({ loading: true });

  try {
    // --- call main metrics endpoint ---
    const resMain = await fetch(`/api/${level}_data_arrow/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
    if (!resMain.ok) throw new Error(`metrics fetch ${resMain.status}`);
    const bufMain = await resMain.arrayBuffer();
    const tableMain = tableFromIPC(bufMain);
    const rowsMain = rowsFromTable(tableMain);

    // --- update state with both ---
    this.setState({
      metricRows: rowsMain,
      loading: false,
    });
    if (rowsMain.length) this._setCache(`metrics:${reqKey}`, rowsMain);
    // Empty result: release the dedupe key so a retry is not swallowed.
    else if (this._lastKey === reqKey) this._lastKey = null;
  } catch (e) {
    if (e.name === "AbortError") {
      // Superseded by a newer request: leave rows/loading to the successor
      // (nulling them here lied `loading: false` to readiness checks), and
      // release the dedupe key so an identical retry isn't swallowed.
      if (this._lastKey === reqKey) this._lastKey = null;
      return;
    }
    console.error("metrics error:", e);
    // Real failure: allow an immediate identical retry instead of the 700ms
    // dedupe silently dropping it with nothing in flight.
    if (this._lastKey === reqKey) this._lastKey = null;
    this.setState({ metricRows: null,  loading: false });
  } finally {
    // Only clear the shared slot if this request still owns it — an aborted
    // predecessor must not clobber the successor's controller.
    if (this._inflight === ctrl) this._inflight = null;
  }

  }
  fetchSecondaryMetrics = async (level, stateCode) => {
    const group = this.getActiveGroup();
    if (!group || (!group.keys?.length && this.props.ActionMode==="explore") || (!group?.TargetVariable1?.keys?.length && this.props.ActionMode==="filter")) {
      this.props.handlepoliInfoChange(null)
      this.props.handlepoliInfoToplevelChange(null)
      return;
    }
  
  
    try {
      
      // --- call secondary metrics endpoint ---
      const resSecondary = await fetch(`/api/${level}_political_data_arrow/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stateCode }),
        //signal: this._inflight.signal,
      });
      if (!resSecondary.ok) throw new Error(`secondary metrics fetch ${resSecondary.status}`);
      const bufSecondary = await resSecondary.arrayBuffer();
      const tableSecondary = tableFromIPC(bufSecondary);
      const rowsSecondary = rowsFromTable(tableSecondary); 
      let toplevel 
          if (stateCode == null || isNaN(stateCode)){
            toplevel = "national"
          }else{
            toplevel = "states"
          }
          const resSecondarytoplevel = await fetch(`/api/${toplevel}_political_data_arrow/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ keys: group.keys, stateCode }),
            //signal: this._inflight.signal,
          });
          if (!resSecondarytoplevel.ok) throw new Error(`secondary metrics fetch ${resSecondarytoplevel.status}`);
          const bufSecondarytoplevel = await resSecondarytoplevel.arrayBuffer();
          const tableSecondarytoplevel = tableFromIPC(bufSecondarytoplevel);
          const rowsSecondarytoplevel = rowsFromTable(tableSecondarytoplevel);
          // use robust parser
  
      // --- update state with both ---
      this.props.handlepoliInfoToplevelChange(rowsSecondarytoplevel)
      this.props.handlepoliInfoChange(rowsSecondary)

    } catch (e) {
      if (e.name !== "AbortError") console.error("metrics error:", e);
      this.props.handlepoliInfoChange(null)
      this.props.handlepoliInfoToplevelChange(null)
        
    } finally {
      //this._inflight = null;
    }
    };


  getGroups = () => this.props.groupsById || {};
  getConcentrationSet = (byId, features, group, subVar, concentration) =>
    _getConcentrationSet(byId, features, group, subVar, concentration, this.props.geoLevel);
  getActiveGroup = () => {
    const groupsById = this.getGroups();
   
    if(this.props.ActionMode==="explore"){
      const id = this.props.selectedGroupId;
      return groupsById?.[id] || null;
    } else if(this.props.ActionMode==="filter"){
    const id = this.props.ActionTargetVariables;

    const g1 = groupsById?.[id.TargetVariable1] || null;
    return { TargetVariable1: g1 };

    }else{
      return null
    }
    
  };

  normalizeAreaIdForLevel = (level, id) => _normalizeAreaIdForLevel(level, id);

  zoomToStateCode = (stateCode) => {
    const map = this.mapRef.current?.getMap();
    if (!map) return;
    const fc = this.state.styledGeo || this.state.geoData;
    if (!fc?.features) return;
    const code = String(stateCode).padStart(2, "0");
    // Match features belonging to this state (STATEFP or leading digits of area_id/GEOID)
    const stateFeatures = fc.features.filter(f => {
      const p = f.properties || {};
      return (
        String(p.STATEFP || "").padStart(2, "0") === code ||
        String(p.area_id || "").substring(0, 2) === code
      );
    });
    if (!stateFeatures.length) return;
    const bounds = new mapboxgl.LngLatBounds();
    const pushCoords = (coords) => {
      if (!coords) return;
      if (typeof coords[0] === "number") { bounds.extend(coords); return; }
      coords.forEach(c => pushCoords(c));
    };
    stateFeatures.forEach(f => pushCoords(f.geometry?.coordinates));
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 40, duration: 800 });
    }
  };

  findFeatureForAreaId = (level, areaId, features = []) =>
    _findFeatureForAreaId(level, areaId, features);

  getFeatureDisplayName = (level, props) => _getFeatureDisplayName(level, props);

  getFeatureCentroid = (feature) => _getFeatureCentroid(feature);
  _applyFeatureState = () => {
    const map = this.mapRef.current?.getMap();
    if (!map || !map.getSource("geo")) return;
    const { styledGeo } = this.state;
    if (!styledGeo?.features) {
      try { map.removeFeatureState({ source: "geo" }); } catch {}
      return;
    }
    for (const f of styledGeo.features) {
      if (f.id == null) continue;
      map.setFeatureState(
        { source: "geo", id: f.id },
        { fillColor: f.properties?.fillColor ?? null }
      );
    }
  };

  restyle = () => {
    const group = this.getActiveGroup();
    const result = _computeRestyle({
      geoData:                   this.state.geoData,
      metricRows:                this.state.metricRows,
      compareMetricRows:         this.state.compareMetricRows,
      compareWavePair:           this.state.compareWavePair,
      ActionMode:                this.props.ActionMode,
      mode:                      this.props.mode,
      preferenceFilter:          this.props.preferenceFilter,
      heatDomain:                this.props.heatDomain,
      heatColor:                 this.props.heatColor,
      topStrengthShading:        this.props.topStrengthShading,
      ActionTargetSubVariables:  this.props.ActionTargetSubVariables,
      ActionTargetConcentration: this.props.ActionTargetConcentration,
      colorLabels:               this.props.colorLabels,
      colorMap:                  this.props.colorMap,
      compareWaves:              this.props.compareWaves,
      compareKey:                this.props.compareKey,
      waveSelect:                this.props.waveSelect,
      waveOptions:               this.props.waveOptions,
      waveFilter:                this.props.waveFilter,
      geoLevel:                  this.props.geoLevel,
      group,
    });

    // ── Channel 5: synchronous, before setState (filter mode only) ───────────
    if (result.maxRangesToSet !== null) {
      this.props.handleActionTargetMAXRangesChange(result.maxRangesToSet);
    }

    // ── Channels 6+7: deferred via setTimeout (tracking schema only) ─────────
    if (result.waveOptionsToSet !== null) {
      const captured = result.waveOptionsToSet;
      setTimeout(() => this.props.handlewaveOptionsChange(captured), 0);
    }
    if (result.waveSelectToSet !== null) {
      const first = result.waveSelectToSet;
      setTimeout(() => this.props.handlewaveSelectChange(first), 0);
    }

    // ── Channels 1+2+3: setState (three shapes matching original exit paths) ──
    if (result.ranked === null) {
      // !geoData early exit — only styledGeo set; ranked/compareArrows untouched in state
      this.setState({ styledGeo: result.styledGeo });
      return;
    }
    this.setState(
      { styledGeo: result.styledGeo, ranked: result.ranked ?? [], compareArrows: result.compareArrows },
      () => {
        // ── Channel 4: synchronous in setState callback ───────────────────────
        if (result.rankedToNotify !== null) {
          this.props.onRankedUpdate?.(this.state.ranked || []);
        }
      }
    );
  };
  
  onclearSelection = () => {
    this.setState({ selectedFeature: null });
  };

  goBackOneLevel = () => {
    const {  selectedStateCode, selectedStateBounds } = this.state;
    const geoLevel = this.props.geoLevel
    const map = this.mapRef.current?.getMap();

    let next;

    if (geoLevel === "zipcodes") {
      next = "counties";
    } else if (geoLevel === "counties" || geoLevel === "cd") {
      next = "states";
    } else if (geoLevel === "states") {
      // 👇 already at state view → go fully out
      this.resetView();
      return;
    }

    // Update level
    this.handleGeoLevelChange(next);

    // If we have a state, reset selectedFeature to the state
    if (selectedStateCode) {
      const stateLabel = stateFipsToName[selectedStateCode] || `State ${selectedStateCode}`;
      this.setState({
        selectedFeature: {
          level: "states",
          key: "STATEFP",
          value: selectedStateCode,
          name: stateLabel,
        }
      });
    } else {
      // If no state lock, just clear selection
      this.setState({ selectedFeature: null });
    }

    const data = this.getAggregatesForSidebar();
    const selectedStateName = this.state.selectedStateCode
      ? stateFipsToName[this.state.selectedStateCode]
      : null;
    this.props.onFeatureSelect?.({ feature: this.state.selectedFeature, data, selectedStateName });
    

    // Optional: move the camera nicely
    if (selectedStateBounds) {
      map?.fitBounds(selectedStateBounds, { padding: 28, duration: 500 });
    }
  };

getAggregatesForSidebar = () => _getAggregatesForSidebar({
    selectedFeature:            this.state.selectedFeature,
    selectedStateCode:          this.state.selectedStateCode,
    metricRows:                 this.state.metricRows,
    metricRowsStates:           this.state.metricRowsStates,
    metricRowsState:            this.state.metricRowsState,
    metricRowsStatesUnfiltered: this.state.metricRowsStatesUnfiltered,
    metricRowsUnfiltered:       this.state.metricRowsUnfiltered,
    compareRowsLatest:          this.state.compareRowsLatest,
    compareRowsPrev:            this.state.compareRowsPrev,
    compareWavePair:            this.state.compareWavePair,
    geoLevel:                   this.props.geoLevel,
    ActionMode:                 this.props.ActionMode,
    theme_colours:              this.props.theme_colours,
    compareWaves:               this.props.compareWaves,
    waveSelect:                 this.props.waveSelect,
    waveFilter:                 this.props.waveFilter,
    preferenceFilter:           this.props.preferenceFilter,
    ActionTargetSubVariables:   this.props.ActionTargetSubVariables,
    group:                      this.getActiveGroup?.(),
    groupsById:                 this.getGroups(),
  });

  handleselectedFeatureChange = ({ level,value,name }) => {
      console.log("[DEBUG] handleSelectedFeatureChange called", { level, value, name });
      console.log("[DEBUG] Example feature props:", this.state.styledGeo?.features?.[0]?.properties);
    
    let key
    switch(level){
      case "states":
        key = "STATEFP"
        break
      case "zipcodes":
        key = "GEOID20"
        break
        case "statesenate":
          key = "GEOID"
          break
      default:
        key = "area_id"
        break
    }

    let feature;

    if (level === "zipcodes") {
      feature = this.state.styledGeo?.features?.find(
        (f) => String(f.properties.GEOID20) === String(value)
      );
    } else if (["statesenate", "statehouse"].includes(level)) {
      feature = this.state.styledGeo?.features?.find(
        (f) => String(f.properties.area_id) === String(value) ||
               String(f.properties.GEOID) === String(value)
      );
    } else {
      feature = this.state.styledGeo?.features?.find(
        (f) => String(f.properties.GEOID) === String(value)
      );
    }
    
    if (!feature) return;

    const currlevel = this.props.geoLevel;
    const p = feature.properties || {};
    const areaId = String(p.area_id ?? "");
  
    const zoomToFeature = (feat) => this.fitBoundsFromFeatures([feat]);

    // Helper: call both metrics if available
    const callAllMetrics = (lvl, scOrNull) => {
      this.fetchMetrics(lvl, scOrNull);
      if (typeof this.fetchSecondaryMetrics === "function") {
        this.fetchSecondaryMetrics(lvl, scOrNull);
      }
    };

    
    // Clicked a state while at "states" level → drill to counties and load metrics
    if (currlevel === "states") {
      const stateCode = String(p.STATEFP || "").padStart(2, "0");
      if (!stateCode) return;
  
      const stateLabel = stateFipsToName[stateCode] || p.NAME || `State ${stateCode}`;
      const stateBounds = zoomToFeature(feature); // sets map to state
  
      this.setState(
        {
          selectedStateCode: stateCode,
          selectedStateBounds: stateBounds,
          // Keep selectedFeature pointing to the state context
          selectedFeature: {
            level: "states",
            key: "STATEFP",
            value: stateCode,
            name: stateLabel,
            rowMatchIds: [stateCode],
          },
        },
        () => {
          const THEME_NEXT_LEVEL = {
            phrma: "statesenate",
            stateleg: "statesenate",
            example_client: "cd",
          };
          const nextLevel = THEME_NEXT_LEVEL[this.props.selectedSection] || "cd";
          this.props.handlegeoLevelChange(nextLevel);
          // Notify parent
          const data = this.getAggregatesForSidebar();
          const selectedStateName = this.getSelectedStateName();
          this.props.onFeatureSelect?.({ feature: null, data, selectedStateName });
        }
      );
      return;
    }
 
    // Normal zoom for county/CD/zip clicks
    if (currlevel === "counties" || currlevel === "cd") {
      zoomToFeature(feature);
    }

    // Build label + rowMatchIds for selection (kept from your version)
    let label;
    if (level === "zipcodes") {
      label = `${feature.properties?.GEOID20 ?? areaId} — ${feature.properties?.NAME ?? ""}`.trim();
    } else {
      label = feature.properties?.NAME || feature.properties?.name || areaId;
    }
  
    const sc  = String(feature.properties?.STATEFP || "").padStart(2, "0");
    const cd2 = String(
      feature.properties?.CD119FP ??
      feature.properties?.CD120FP ??
      feature.properties?.CD118FP ??
      feature.properties?.CD116FP ??
      ""
    ).padStart(2, "0");
    const cnty = String(feature.properties?.COUNTYFP ?? "").padStart(3, "0");
  



    let rowMatchIds = [areaId];
    if (level === "cd" && sc && cd2) {
      rowMatchIds.push(sc + cd2, `${sc}-${cd2}`);
    }
    if (level === "counties" && sc && cnty) {
      rowMatchIds.push(sc + cnty); // 5-digit FIPS
    }
  


    
    // If we’re at counties/CD but no state yet, infer it, set state context, and load metrics/geo
    if ((currlevel === "counties" || currlevel === "cd") && !this.state.selectedStateCode) {
      const inferredStateCode = String(p.STATEFP || "").padStart(2, "0");
      if (!inferredStateCode) return;
  
      zoomToFeature(feature);
      const stateBounds = this.computeStateBoundsFromCurrentGeoData(inferredStateCode);
  
      this.setState(
        {
        selectedFeature: {
          level: level,
          key: key,
          value: areaId,
          name: name,
          rowMatchIds,
        },
          selectedStateCode: inferredStateCode,
          selectedStateBounds: stateBounds,
        },
        () => {
          this.props.handlegeoLevelChange(currlevel);
        }
      );
      return;
    }

    this.setState(
      {
        selectedFeature: {
          level: level,
          key: key,
          value: areaId,
          name: label,
          rowMatchIds,
        },
      },
      () => {
        const data = this.getAggregatesForSidebar();
        const selectedStateName = this.getSelectedStateName();
        const selectedAreaLabel = this.state.selectedFeature?.name || null;
        const selectedFeatureLevel = this.state.selectedFeature?.level || null;
  
        this.props.onFeatureSelect?.({
          feature: this.state.selectedFeature,
          data,
          selectedStateName,
          selectedAreaLabel,
          selectedFeatureLevel,
        });
      }
    );
  


      }


resetView = ({ LevelToResetTo = "states" } = {}) => {
  
  if(LevelToResetTo==="zipcodes"){
    LevelToResetTo="states"
  }
  if (this._inflight) {
    this._inflight.abort();
    this._inflight = null;
  }
  this._lastKey = null;
  this._lastAt = 0;
  this.setState({
    selectedStateCode: null,
    selectedStateBounds: null,
    selectedFeature: null,
    geoData: null,
    metricRows: null,
    styledGeo: null,
  }, () => {
    this.loadGeo(LevelToResetTo, null, { doFit: true });
    this.fetchMetrics(LevelToResetTo, null);
    this.fetchSecondaryMetrics(LevelToResetTo, null);
      

    const data = this.getAggregatesForSidebar();
    this.props.onFeatureSelect?.({ feature: null, data, selectedStateName: null });
  });
  
    this.props.handlegeoLevelChange(LevelToResetTo)
};

/** Update Mapbox feature-state for chat highlights (clears old, sets new) */
_applyChatFeatureState = (prevAreas, nextAreas) => {
  const map = this.mapRef.current?.getMap?.();
  if (!map || !map.getSource("geo")) return;
  const level = this.props.geoLevel;
  const norm = (id) => this.normalizeAreaIdForLevel(level, id);

  // Build lookup keyed by every plausible normalised form of the area_id
  const features = this.state.geoData?.features || [];
  const idByAreaId = {};
  features.forEach((f) => {
    if (f.id != null && f.properties?.area_id) {
      // Index under the raw value AND the normalised value so either format matches
      idByAreaId[f.properties.area_id] = f.id;
      idByAreaId[norm(f.properties.area_id)] = f.id;
    }
  });
  (prevAreas || []).forEach((code) => {
    const fid = idByAreaId[code] ?? idByAreaId[norm(code)];
    if (fid != null) {
      try { map.setFeatureState({ source: "geo", id: fid }, { chatHighlight: false }); } catch (_) {}
    }
  });
  (nextAreas || []).forEach((code) => {
    const fid = idByAreaId[code] ?? idByAreaId[norm(code)];
    if (fid != null) {
      try { map.setFeatureState({ source: "geo", id: fid }, { chatHighlight: true }); } catch (_) {}
    }
  });
};

zoomToChatAreas = (areaCodes) => {
  if (!areaCodes?.length || !this.state.geoData?.features) return;
  const level = this.props.geoLevel;
  const norm = (id) => this.normalizeAreaIdForLevel(level, id);
  const codeSet = new Set([...areaCodes, ...areaCodes.map(norm)]);
  const features = this.state.geoData.features.filter(
    (f) => codeSet.has(f.properties?.area_id) || codeSet.has(norm(f.properties?.area_id)) || codeSet.has(f.properties?.GEOID)
  );
  if (features.length > 0) {
    this.fitBoundsFromFeatures(features);
  }
};

fitBoundsFromFeatures = (features) => {
  if (!this.mapRef.current || !features?.length) return null;
  const map = this.mapRef.current.getMap();
  const bounds = new mapboxgl.LngLatBounds();
  features.forEach(f => {
    const coords = f.geometry.type === "Polygon"
      ? f.geometry.coordinates.flat(1)
      : f.geometry.coordinates.flat(2);
    coords.forEach(([lng, lat]) => bounds.extend([lng, lat]));
  });
  map.fitBounds(bounds, { padding: 28, duration: 500 });
  return bounds;
};

computeStateBoundsFromCurrentGeoData = (stateCode) => {
  const sc = String(stateCode).padStart(2, "0");
  const feats = this.state.geoData?.features?.filter(
    f => String(f.properties?.STATEFP).padStart(2, "0") === sc
  ) || [];
  if (!feats.length) return null;

  const bounds = new mapboxgl.LngLatBounds();
  feats.forEach(f => {
    const coords = f.geometry.type === "Polygon"
      ? f.geometry.coordinates.flat(1)
      : f.geometry.coordinates.flat(2);
    coords.forEach(([lng, lat]) => bounds.extend([lng, lat]));
  });
  return bounds;
};


  onMouseMove = (evt) => {
    if (this.props.isSidebarHover) {
      if (this.state.hoverInfo) {
        this.setState({ hoverInfo: null });
      }
      this.props.handlehoverAreaChange?.(null);
      return;
    }
    const feature = evt.features && evt.features[0];
    if (!feature){
      
      this.setState({ hoverInfo: null })
      this.props.handlehoverAreaChange(null)
      return
    };
  
    const { lng, lat } = evt.lngLat;
    const styledProps = (feature.id != null && this.state.styledGeo?.features[feature.id]?.properties)
      ? this.state.styledGeo.features[feature.id].properties
      : feature.properties;
    this.setState({
      hoverInfo: { lon: lng, lat, props: styledProps, x: evt.point.x, y: evt.point.y }
    });
    this.props.handlehoverAreaChange(feature.properties.area_id ?? null)
  };

  onMouseLeave = () => {
    if (this.state.hoverInfo) {
      this.setState({ hoverInfo: null });
    }
    this.props.handlehoverAreaChange?.(null);
  };

  onMapMoveEnd = () => {
    // No-op: geo is loaded with full-US bounds and cached, so no need to reload on pan/zoom.
  };



  render() {
    const {  styledGeo, geoData, hoverInfo, metricRowsUnfiltered } = this.state;
    const geoLevel = this.props.geoLevel
    const active_group = this.getActiveGroup()

    // Chat highlight layers use feature-state ("chatHighlight") so expressions are static.
    const hasChatHighlight = (this.props.chatHighlightAreas?.length ?? 0) > 0;
    const chatDimOpacity = ["case", ["boolean", ["feature-state", "chatHighlight"], false], 0, hasChatHighlight ? 0.62 : 0];
    const chatFillOpacity = ["case", ["boolean", ["feature-state", "chatHighlight"], false], hasChatHighlight ? 0.08 : 0, 0];
    const chatBorderFilter = ["boolean", ["feature-state", "chatHighlight"], false];

    // console.log("selectedSection prop:", this.props.selectedSection);

    const allowedLevels = getSelectableLevels(
      this.props.selectedSection,
      !!this.state.selectedStateCode,
      allowNationalStatelegForTheme(this.props.selectedSection)
    );

    // Which congressional-district vintage is on screen (only meaningful at the
    // "cd" level). Chosen automatically from the active question's schema + wave.
    // Suppressed in the highlights-embedded map (hideToolbar), where the reels
    // place their own controls in that corner.
    const cdInfo = geoLevel === "cd" && !this.props.hideToolbar
      ? cdVersionInfoFor(
          (this.props.ActionMode === "filter" ? active_group?.TargetVariable1?.schema : active_group?.schema) || "public",
          this.props.waveSelect ?? this.props.waveFilter
        )
      : null;
    return (
      <div style={{ height: "100%", width: "100%", position: "relative" }}>
        {cdInfo && (
          <div
            title={`Boundaries: ${cdInfo.label} congressional districts`}
            style={{
              position: "absolute",
              bottom: 10,
              left: 10,
              zIndex: 1200,
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 9px",
              borderRadius: 999,
              fontSize: "0.72rem",
              fontWeight: 600,
              fontFamily: "'DM Sans', sans-serif",
              color: "#334155",
              background: "rgba(255,255,255,0.82)",
              border: "1px solid rgba(15,23,42,0.10)",
              boxShadow: "0 2px 8px rgba(15,23,42,0.08)",
              backdropFilter: "blur(6px)",
              pointerEvents: "none",
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#0f9fab" }} />
            {cdInfo.label} districts
          </div>
        )}
        {this.state.loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 2100,
              background: "rgba(248,249,252,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 12,
              backdropFilter: "blur(3px)",
            }}
          >
            <div
              style={{
                width: 84,
                height: 84,
                borderRadius: "20px",
                background: "rgba(255,255,255,0.9)",
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div className="map-loader-ring" />
            </div>
            <div style={{ fontSize: 12, color: "#334155", fontWeight: 600, letterSpacing: "0.02em" }}>
              Loading data
            </div>
            <div className="map-loader-dots" />
          </div>
        )}
        {/* Chat highlight badge — visible on Data tab when highlights are active.
            Suppressed entirely in the highlights embed (hideToolbar): the reels
            keep the map free of overlays, drive their own highlights from the
            narrative cards rather than Ask AI, and own the clear affordance. */}
        {hasChatHighlight && !this.props.hideToolbar && (
          <div style={{
            position: "absolute",
            bottom: 20,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            gap: 8,
            backgroundColor: "rgba(255,255,255,0.97)",
            border: "1px solid rgba(15,159,171,0.3)",
            borderRadius: 999,
            boxShadow: "0 4px 16px rgba(15,23,42,0.1)",
            padding: "6px 10px 6px 14px",
            fontFamily: "'DM Sans', sans-serif",
            whiteSpace: "nowrap",
          }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#0f9fab", flexShrink: 0 }} />
            <span style={{ fontSize: 12, fontWeight: 500, color: "#0f172a" }}>
              {this.props.chatHighlightAreas.length} area{this.props.chatHighlightAreas.length !== 1 ? "s" : ""} highlighted from Ask AI
            </span>
            <button
              onClick={this.props.onClearHighlights}
              style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 11, color: "#64748b", padding: "2px 6px",
                borderRadius: 6, fontFamily: "'DM Sans', sans-serif",
                marginLeft: 2,
              }}
              onMouseEnter={e => { e.currentTarget.style.color = "#0f172a"; e.currentTarget.style.backgroundColor = "rgba(15,23,42,0.05)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "#64748b"; e.currentTarget.style.backgroundColor = "transparent"; }}
            >
              Clear ✕
            </button>
          </div>
        )}

        {this.state.selectedFeature?.name && (
  <div
    style={{
      position: "absolute",
      top: 12,
      left: 12,
      background: "rgba(255,255,255,0.95)",
      padding: "6px 10px",
      borderRadius: 6,
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: 13,
      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
      display: "flex",
      alignItems: "center",
      gap: 8
    }}
  >
    <strong>Selected:</strong> {this.state.selectedFeature.name}
    <button
      onClick={() => this.setState({ selectedFeature: null })}
      style={{
        border: "none",
        background: "transparent",
        cursor: "pointer",
        fontSize: 16,
        lineHeight: 1,
        color: "#666",
        marginLeft: 6
      }}
      aria-label="Clear selection"
      title="Clear selection"
    >
      ×
    </button>
  </div>
)}

              <ReactMap
                  ref={this.mapRef}
                  initialViewState={{ longitude: -98, latitude: 39, zoom: 3.5 }}
                  style={{ width: "100%", height: "100%" }}
                  projection={{ name: 'mercator' }}
                  mapStyle={MAP_STYLE}
                  mapboxAccessToken={MAPBOX_TOKEN}
                  interactiveLayerIds={["geo-fill"]}
                  renderWorldCopies={false}
                  onClick={this.props.disableGeoClick ? undefined : this.onMapClick}
                  onMouseMove={this.onMouseMove}
                  onMouseLeave={this.onMouseLeave}
                  onLoad={this.onStyleLoad}
                  onMoveEnd={this.onMapMoveEnd}
                  dragPan={!this.props.disableInteraction}
                  scrollZoom={!this.props.disableInteraction}
                  dragRotate={!this.props.disableInteraction}
                  doubleClickZoom={!this.props.disableInteraction}
                  touchZoomRotate={!this.props.disableInteraction}
                >
              {this.state.styleLoaded && (
                  <Source id="geo" type="geojson" data={(() => {
                    if (!geoData) return { type: "FeatureCollection", features: [] };
                    if (!this.props.geoStateFilter) return geoData;
                    const code = String(this.props.geoStateFilter).padStart(2, "0");
                    return { ...geoData, features: geoData.features.filter(f => String(f.properties?.STATEFP || "").padStart(2, "0") === code) };
                  })()} tolerance={window.innerWidth < 600 ? 1 : 0.5}>
          <Layer
            id="geo-fill"
            type="fill"
            paint={{
              "fill-color": ["coalesce", ["feature-state", "fillColor"], "#ffffff"],
              "fill-opacity": [
                "case", ["!=", ["feature-state", "fillColor"], null], 0.65, 0
              ]
            }}
          />
          <Layer
            id="geo-outline"
            type="line"
            paint={{
              "line-color": "#444",
              "line-width": 0.25,
              // "line-opacity": [
              //   "case", ["has", "fillColor"], 0.025, 0
              // ]
            }}
          />
          {hoverInfo && (
          <Layer
            id="geo-hover"
            type="line"
            paint={{
              "line-color": "#ffffff",
              "line-width": 2
            }}
            filter={[
              "==",
              ["get", JOIN_KEY_MAP[this.props.geoLevel]?.shapefile || "GEOID"],
              hoverInfo?.props?.[JOIN_KEY_MAP[this.props.geoLevel]?.shapefile || "GEOID"] || ""
            ]}  />
          )} 
        <Layer
          id="geo-selected-halo"
          type="line"
          paint={{
            "line-color": "#ffffff",
            "line-width": 4
          }}
          filter={
            this.state.selectedFeature?.level === this.props.geoLevel &&
            this.state.selectedFeature?.value
              ? ["==", ["get", this.state.selectedFeature.key], this.state.selectedFeature.value]
              : ["==", ["get", "__none__"], "__none__"] // always-false fallback
          }
        />

        <Layer
          id="geo-selected"
          type="line"
          paint={{
            "line-color": "#1976d2",
            "line-width": 2
          }}
          filter={
            this.state.selectedFeature?.level === this.props.geoLevel &&
            this.state.selectedFeature?.value
              ? ["==", ["get", this.state.selectedFeature.key], this.state.selectedFeature.value]
              : ["==", ["get", "__none__"], "__none__"]
          }
        />

        {/* ── Chat highlight layers — always mounted, controlled via paint/filter ── */}
        {/* 1. Dim overlay on non-highlighted areas */}
        <Layer
          id="chat-dim"
          type="fill"
          paint={{
            "fill-color": "#f0f4ff",
            "fill-opacity": chatDimOpacity,
            "fill-opacity-transition": { duration: 350, delay: 0 },
          }}
        />
        {/* 2. Subtle blue tint on highlighted areas */}
        <Layer
          id="chat-highlight-fill"
          type="fill"
          paint={{
            "fill-color": "#1d4ed8",
            "fill-opacity": ["case", ["boolean", ["feature-state", "chatHighlight"], false], chatFillOpacity, 0],
            "fill-opacity-transition": { duration: 350, delay: 0 },
          }}
        />
        {/* 3. Soft glow border */}
        <Layer
          id="chat-highlight-glow"
          type="line"
          paint={{
            "line-color": "#3b82f6",
            "line-width": 6,
            "line-opacity": ["case", ["boolean", ["feature-state", "chatHighlight"], false], 0.25, 0],
          }}
        />
        {/* 4. Crisp inner border */}
        <Layer
          id="chat-highlight-border"
          type="line"
          paint={{
            "line-color": "#1d4ed8",
            "line-width": 2,
            "line-opacity": ["case", ["boolean", ["feature-state", "chatHighlight"], false], 1, 0],
          }}
        />

        </Source>

          )}

              {this.state.styleLoaded && this.props.compareWaves && this.state.compareArrows && this.state.showArrows && (
                <Source id="compare-arrows" type="geojson" data={this.state.compareArrows}>
                  <Layer
                    id="compare-arrows-layer"
                    type="symbol"
                    layout={{
                      "icon-image": "compare-arrow",
                      "icon-rotate": ["get", "angle"],
                      "icon-size": ["get", "size"],
                      "icon-allow-overlap": true,
                      "icon-ignore-placement": true,
                      "icon-rotation-alignment": "map",
                      "icon-anchor": "center",
                    }}
                    paint={{
                      "icon-color": ["get", "color"],
                      "icon-opacity": ["get", "opacity"],
                      "icon-halo-color": "rgba(255,255,255,0.6)",
                      "icon-halo-width": 1.5,
                    }}
                  />
                </Source>
              )}


{hoverInfo && !this.props.isSidebarHover && (() => {
  // ── Pre-compute everything so JSX stays clean ──────────────────────────────

  const hProps = hoverInfo.props || {};

  // Area label
  const isZip = this.props.geoLevel === "zipcodes";
  const areaName = isZip && hProps.GEOID20
    ? `ZIP ${hProps.GEOID20}`
    : hProps.NAME || hProps.name || "Unknown Area";
  const areaSub = isZip && hProps.NAME ? hProps.NAME : null;

  // Response bars
  let items = hProps._allValuesSorted;
  if (typeof items === "string") { try { items = JSON.parse(items); } catch { items = null; } }
  if (!Array.isArray(items)) {
    const obj = hProps._allValues;
    items = obj && typeof obj === "object"
      ? Object.entries(obj).map(([key, label, value]) => ({ key, label, value }))
      : [];
  }
  const group    = this.getActiveGroup?.();
  const colors   = this.props.colorMap || {};
  const isPrice  = group?.type === "price_elast" || group?.TargetVariable1?.type === "price_elast";
  const isCategoricalQuestion = group?.type === "categorical" || group?.TargetVariable1?.type === "categorical";
  const fmtVal   = (v) => isPrice ? `$${(v / 100).toFixed(2)}` : `${v.toFixed(1)}%`;
  const compareSortMode = this.props.compareKey === "__top_neg__" ? "losers" : this.props.compareKey === "__top__" ? "gainers" : null;

  const filteredBars = items
    .filter(({ key, value }) => {
      if (!Number.isFinite(Number(value))) return false;
      if (this.props.ActionMode === "explore") return true;
      if (this.props.ActionMode === "filter") {
        const t = this.props.ActionTargetSubVariables?.TargetSubVariable1;
        return key === t || (t === "net" && key === "net1");
      }
      return false;
    });
  const clampPct = (v) => Math.max(0, Math.min(100, v));

  // Footer: universe + benchmarks + filters
  const filters    = this.getFilters?.() || {};
  const hasFilters = Object.values(filters).some((v) => v != null);
  const inExplore  = this.props.ActionMode === "explore";
  const pref       = this.props.preferenceFilter;

  // Likert polarity highlighting
  const isLikert = group?.type === "likert";
  const toArrL = (v) => Array.isArray(v) ? v : (v ? [v] : []);
  const polarityPos = isLikert ? toArrL(group?.polarity?.pos) : [];
  const polarityNeg = isLikert ? toArrL(group?.polarity?.neg) : [];
  const isNetView = isLikert && (!pref || pref === "net");
  // Highlight whichever option the map is currently painting. Testing membership
  // of the group's own keys covers every type in one expression: a likert option
  // or a categorical category is in `keys` and highlights, while "net" and the
  // "top"/"second"/"third" view modes are not keys, so nothing highlights in the
  // views where no single choice is selected.
  const selectableKeys = (inExplore ? group?.keys : group?.TargetVariable1?.keys) || [];
  const selectedSubKey = pref && selectableKeys.includes(pref) ? pref : null;
  const subVar     = inExplore
    ? (group?.type === "likert" && (!pref || pref === "net") ? "net"
        : (pref && group?.keys?.includes(pref) ? pref : group?.keys?.[0]))
    : this.props.ActionTargetSubVariables?.TargetSubVariable1;

  const toArr    = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
  const sumKeys  = (keys, row) => keys.reduce((a, k) => a + (Number(row?.[k]) || 0), 0);
  const fmtBench = (v) => isPrice ? `$${(v / 100).toFixed(2)}` : `${v.toFixed(1)}%`;

  const unfiltMap  = new Map((metricRowsUnfiltered || []).map((r) => [String(r?.area_id ?? ""), r]));
  const areaId     = String(hProps.area_id ?? "");
  const unfiltRow  = unfiltMap.get(areaId);
  const isStateHover = this.props.geoLevel === "states" &&
    this.state.selectedStateCode &&
    String(areaId).padStart(2, "0") === String(this.state.selectedStateCode).padStart(2, "0");

  let stateBench = null, areaBench = null, filteredBench = null;
  let stateRow = null;
  // "Top" view paints every area by whichever category wins THERE, so there is no
  // common series to benchmark against. subVar falls back to group.keys[0] in that
  // mode, which produced a figure describing one arbitrary category — the wrong
  // number, confidently labelled. Suppress both benchmarks outright.
  const isTopView =
    (this.props.mode && this.props.mode !== "heat") ||
    ["top", "second", "third"].includes(pref);
  const suppressBenches = isCategoricalQuestion && isTopView;
  if (this.state.selectedStateCode && group && subVar && !suppressBenches) {
    const allStateRows = Array.isArray(this.state.metricRowsStatesUnfiltered) ? this.state.metricRowsStatesUnfiltered : [];
    // The unfiltered state baseline is fetched WITHOUT a wave filter on purpose
    // (fetchStatesBaselineUnfiltered nulls it for tracking schemas, because the
    // sidebar's trend charts need every wave). For a tracking schema the backend
    // also adds `wave` to the grouping, so this array holds one row per state PER
    // WAVE. Finding by area_id alone therefore returned whichever wave came back
    // first: Oregon read -12.3 against its true current -30.9. Narrow to the
    // selected wave first, exactly as getAggregatesForSidebar already does.
    const benchSchema = (inExplore ? group?.schema : group?.TargetVariable1?.schema) || "public";
    const benchWave = this.props.waveSelect ?? this.props.waveFilter;
    const stateRows = (isTrackingSchema(benchSchema) && benchWave != null)
      ? allStateRows.filter((r) => String(r?.wave) === String(benchWave))
      : allStateRows;
    stateRow  = stateRows.find((r) => String(r?.area_id ?? "") === String(this.state.selectedStateCode).padStart(2, "0"));
    // For a likert the benchmark is ALWAYS the net, whatever option the map is
    // painting. The net is the likert's headline number, so a benchmark taken off
    // whichever single option happened to be selected sat next to a net and read
    // as simply wrong.
    const benchKey = isLikert ? "net" : subVar;
    const calc = (row) => {
      if (!row) return null;
      if (benchKey === "net") {
        const pos = sumKeys(toArr(inExplore ? group?.polarity?.pos : group?.TargetVariable1?.polarity?.pos), row);
        const neg = sumKeys(toArr(inExplore ? group?.polarity?.neg : group?.TargetVariable1?.polarity?.neg), row);
        return Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : null;
      }
      const v = Number(row?.[benchKey]); return Number.isFinite(v) ? v : null;
    };
    stateBench    = calc(stateRow);
    areaBench     = isStateHover ? calc(stateRow) : calc(unfiltRow);
    if (hasFilters) {
      const fv      = subVar === "net" ? Number(hProps.net1) : Number(hProps[subVar]);
      filteredBench = Number.isFinite(fv) ? fv : null;
    }
  }
  const totalN     = Number((isStateHover ? stateRow : unfiltRow)?.total_sum);
  const filteredN  = hasFilters ? Number(hProps.total_sum) : null;
  const optionBaseN = Number.isFinite(filteredN)
    ? filteredN
    : (Number.isFinite(totalN) ? totalN : null);
  const optionCountFor = (pct) => {
    if (!Number.isFinite(optionBaseN) || !Number.isFinite(pct)) return null;
    return Math.round((optionBaseN * pct) / 100);
  };

  const demoPills = [];
  if (this.props.selectedDemographics) {
    Object.entries(this.props.selectedDemographics).forEach(([key, val]) => {
      const lbls = Array.isArray(val) ? val : val ? [val] : [];
      lbls.forEach((l) => demoPills.push({ key: `${key}-${l}`, text: `${key.charAt(0).toUpperCase() + key.slice(1)}: ${l}` }));
    });
  }

  const nStats = [
    this.state.selectedStateCode && Number.isFinite(totalN)       && { label: "n",          value: Math.round(totalN).toLocaleString(),      blue: false },
    this.state.selectedStateCode && Number.isFinite(filteredN)    && { label: "n (filtered)", value: Math.round(filteredN).toLocaleString(), blue: true  },
  ].filter(Boolean);

  const benchLabel = isLikert ? "net" : "avg";
  const benchStats = [
    Number.isFinite(stateBench) && { label: `state ${benchLabel}`, value: fmtBench(stateBench), blue: false },
    Number.isFinite(areaBench)    && { label: `area ${benchLabel}`, value: fmtBench(areaBench),               blue: false },
    Number.isFinite(filteredBench)&& { label: "filtered",    value: fmtBench(filteredBench),                 blue: true  },
  ].filter(Boolean);

  const hasFooter = nStats.length > 0 || benchStats.length > 0 || demoPills.length > 0;
  const deltaWavePair =
    this.state.compareWavePair ||
    this.getLatestTwoWaves(this.state.metricRows) ||
    this.getLatestTwoWaves(this.state.compareRowsLatest);
  const isCompare = !!this.props.compareWaves && Array.isArray(deltaWavePair) && deltaWavePair.length === 2;

  // Look up the latest-wave row and the delta row for the hovered area
  const compareLatestRow = isCompare
    ? (() => {
        const normId = this.normalizeAreaIdForLevel(this.props.geoLevel, areaId);
        return (this.state.compareRowsLatest || []).find(r =>
          this.normalizeAreaIdForLevel(this.props.geoLevel, r?.area_id) === normId ||
          String(r?.area_id ?? "") === areaId
        ) || null;
      })()
    : null;

  const compareDeltaRow = isCompare
    ? (() => {
        const normId = this.normalizeAreaIdForLevel(this.props.geoLevel, areaId);
        return (this.state.compareMetricRows || []).find(r =>
          String(r?.area_id ?? "") === normId || String(r?.area_id ?? "") === areaId
        ) || null;
      })()
    : null;

  // Compute net delta if group has polarity
  const groupForCompare = isCompare ? (this.getActiveGroup?.() || null) : null;
  const toArrC = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
  const sumKeysC = (keys, row) => keys.reduce((a, k) => a + (Number(row?.[k]) || 0), 0);
  const netDelta = isCompare && compareDeltaRow && groupForCompare
    ? (() => {
        const posKeys = toArrC(
          this.props.ActionMode === "filter"
            ? groupForCompare?.TargetVariable1?.polarity?.pos
            : groupForCompare?.polarity?.pos
        );
        const negKeys = toArrC(
          this.props.ActionMode === "filter"
            ? groupForCompare?.TargetVariable1?.polarity?.neg
            : groupForCompare?.polarity?.neg
        );
        if (!posKeys.length && !negKeys.length) return null;
        const pos = sumKeysC(posKeys, compareDeltaRow);
        const neg = sumKeysC(negKeys, compareDeltaRow);
        return Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : null;
      })()
    : null;

  let deltaValue = null;
  if (isCompare && subVar) {
    const v = subVar === "net" ? Number(hProps.net1 ?? hProps.Net) : Number(hProps[subVar]);
    deltaValue = Number.isFinite(v) ? v : null;
  }
  const compareMovers = isCompare && compareDeltaRow && groupForCompare
    ? (() => {
        const moverGroup = this.props.ActionMode === "filter"
          ? groupForCompare?.TargetVariable1
          : groupForCompare;
        const moverKeys = moverGroup?.keys || [];
        const deltas = moverKeys
          .map((key) => {
            const delta = Number(compareDeltaRow?.[key]);
            if (!Number.isFinite(delta)) return null;
            return {
              key,
              label: moverGroup?.labels?.[key] || key,
              delta,
            };
          })
          .filter(Boolean);
        if (!deltas.length) return null;
        return {
          topGainer: [...deltas].sort((a, b) => b.delta - a.delta)[0] || null,
          topLoser: [...deltas].sort((a, b) => a.delta - b.delta)[0] || null,
        };
      })()
    : null;
  const visibleBars = (() => {
    const enriched = filteredBars.map((item) => {
      const delta = isCompare && compareDeltaRow ? Number(compareDeltaRow?.[item.key]) : null;
      return { ...item, delta: Number.isFinite(delta) ? delta : null };
    });
    if (isCompare && compareSortMode) {
      enriched.sort((a, b) => {
        const da = Number.isFinite(a.delta) ? a.delta : (compareSortMode === "losers" ? Infinity : -Infinity);
        const db = Number.isFinite(b.delta) ? b.delta : (compareSortMode === "losers" ? Infinity : -Infinity);
        return compareSortMode === "losers" ? da - db : db - da;
      });
    }
    return enriched.slice(0, 8);
  })();



  // ── Anchor: place popup in whichever screen quadrant has the most space ────────
  // The Mapbox popup renders in the browser viewport (not clipped to map canvas),
  // so we compare against window dimensions. evt.point gives canvas-relative coords;
  // we get the canvas rect to convert to viewport coords.
  const mapCanvas  = this.mapRef?.current?.getMap()?.getCanvas();
  const mapRect    = mapCanvas?.getBoundingClientRect() ?? { left: 0, top: 0 };
  const vx         = (hoverInfo.x ?? 0) + mapRect.left;   // viewport X
  const vy         = (hoverInfo.y ?? 0) + mapRect.top;    // viewport Y

  // Worst-case height: name + pills + 6 bars + net row + footer = ~420px
  // Add generous buffer for multiple filter pills wrapping
  const PW  = 280;
  const PH  = 500;
  const PAD = 20;

  const goRight = (window.innerWidth  - vx) >= PW + PAD;
  const goLeft  =  vx                        >= PW + PAD;
  const goBelow = (window.innerHeight - vy)  >= PH + PAD;
  const goAbove =  vy                        >= PH + PAD;

  const hDir  = goRight ? "right" : goLeft ? "left" : (window.innerWidth - vx) >= vx ? "right" : "left";
  const vDir  = goBelow ? "below" : goAbove ? "above" : (window.innerHeight - vy) >= vy ? "below" : "above";

  // anchor = which edge/corner of the popup sits at the map point
  // "top-left"     → popup opens right+down  (use when space is right+below)
  // "top-right"    → popup opens left+down   (use when space is left+below)
  // "bottom-left"  → popup opens right+up    (use when space is right+above)
  // "bottom-right" → popup opens left+up     (use when space is left+above)
  const anchor = `${vDir === "below" ? "top" : "bottom"}-${hDir === "right" ? "left" : "right"}`;
  const offX   =  hDir === "right" ?  10 : -10;
  const offY   =  vDir === "below" ?  10 : -10;

  // ── Portal position: translate vx/vy into CSS top/left ─────────────────────
  // hDir/vDir tell us which way the popup opens from the cursor.
  // We position the corner that sits at the cursor, then the card extends away.
  // PHONES (<600px): neither side has PW+PAD of room, so flanking pushes the
  // card off-screen (worst for western districts). Center it on the tap point
  // and clamp it inside the viewport instead; vertical logic is unchanged.
  const isPhoneViewport = window.innerWidth < 600;
  const cardW = isPhoneViewport ? Math.min(260, window.innerWidth - 24) : 260;
  const posStyle = {
    position:   "fixed",
    zIndex:     99999,          // above everything — navbar, sidebar, modals
    pointerEvents: "none",
    ...(vDir === "below" ? { top:  vy + 10 } : { bottom: window.innerHeight - vy + 10 }),
    ...(isPhoneViewport
      ? { left: Math.max(12, Math.min(vx - cardW / 2, window.innerWidth - cardW - 12)) }
      : (hDir === "right" ? { left: vx + 10 } : { right:  window.innerWidth  - vx + 10 })),
  };

  return ReactDOM.createPortal(
    <div style={posStyle}>
      {/* Card */}
      <div style={{
        width: cardW,
        fontFamily: "'DM Sans', system-ui, sans-serif",
        background: "#ffffff",
        borderRadius: 10,
        boxShadow: "0 4px 20px rgba(0,0,0,0.13), 0 1px 4px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}>

        {/* ── Header ── */}
        <div style={{
          padding: "10px 13px 8px",
          borderBottom: "1px solid #f0f2f7",
        }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#1a1d23", lineHeight: 1.3 }}>
            {areaName}
          </div>
          {areaSub && (
            <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 1 }}>{areaSub}</div>
          )}
          {isCompare && Array.isArray(deltaWavePair) && (
            <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 5 }}>
              <div style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: "rgba(25,118,210,0.07)",
                border: "1px solid rgba(25,118,210,0.15)",
                borderRadius: 5,
                padding: "2px 7px",
                fontSize: 10,
                color: "#1976d2",
                fontWeight: 600,
              }}>
                <span style={{ opacity: 0.7 }}>Wave {deltaWavePair[1]}</span>
                <span style={{ fontSize: 9 }}>→</span>
                <span>Wave {deltaWavePair[0]}</span>
              </div>
              {compareMovers?.topGainer && (
                <div style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: "rgba(22,163,74,0.09)",
                  border: "1px solid rgba(22,163,74,0.18)",
                  borderRadius: 5,
                  padding: "2px 7px",
                  fontSize: 10,
                  color: "#15803d",
                  fontWeight: 600,
                }}>
                  <span style={{ opacity: 0.75 }}>Top gainer</span>
                  <span>{compareMovers.topGainer.label}</span>
                </div>
              )}
              {compareMovers?.topLoser && (
                <div style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: "rgba(220,38,38,0.08)",
                  border: "1px solid rgba(220,38,38,0.16)",
                  borderRadius: 5,
                  padding: "2px 7px",
                  fontSize: 10,
                  color: "#b91c1c",
                  fontWeight: 600,
                }}>
                  <span style={{ opacity: 0.75 }}>Top loser</span>
                  <span>{compareMovers.topLoser.label}</span>
                </div>
              )}
            </div>
          )}
          {demoPills.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
              {demoPills.map(({ key, text }) => (
                <div key={key} style={{
                  fontSize: 10, fontWeight: 600,
                  color: "#0369a1",
                  background: "rgba(3,105,161,0.08)",
                  border: "1px solid rgba(3,105,161,0.28)",
                  borderRadius: 4,
                  padding: "1px 5px",
                  lineHeight: 1.6,
                }}>
                  {text}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Bars ── */}
        <div style={{ padding: "9px 13px", borderBottom: hasFooter ? "1px solid #f0f2f7" : "none" }}>
          {visibleBars.length === 0 ? (
            <div style={{ fontSize: 11, color: "#9ca3af" }}>No data</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {visibleBars.filter((b) => !(b.key === "net1" || b.key === "Net")).map(({ key, label, value }) => {
                const num      = Number(value);
                const barColor = colors[key] || "#2563eb";
                const isNet    = key === "net1" || key === "Net";
                const barW     = clampPct(num);

                const displayLabel = this.props.ActionMode === "explore"
                  ? label
                  : isNet
                    ? "Net: " + (group?.TargetVariable1?.label || "")
                    : (label || group?.TargetVariable1?.label || "");

                // In compare mode: num = delta; look up current absolute value from latest row
                const currentVal = isCompare && compareLatestRow
                  ? Number(compareLatestRow[key])
                  : null;
                const deltaVal = isCompare
                  ? (compareDeltaRow ? Number(compareDeltaRow[key]) : num)
                  : null;
                const showDelta = isCompare && Number.isFinite(deltaVal);
                const barDisplayNum = isCompare && Number.isFinite(currentVal) ? currentVal : num;
                const barDisplayW   = isCompare && Number.isFinite(currentVal) ? clampPct(currentVal) : barW;
                const optionCount =
                  this.props.ActionMode === "filter" && !isNet
                    ? optionCountFor(barDisplayNum)
                    : null;

                const isPos = isNetView && polarityPos.includes(key);
                const isNeg = isNetView && polarityNeg.includes(key);
                const isSelRow = selectedSubKey === key;
                const rowBg = isPos ? "rgba(22,163,74,0.09)" : isNeg ? "rgba(220,38,38,0.09)" : isSelRow ? "rgba(37,99,235,0.09)" : "transparent";
                const labelColor = isPos ? "#166534" : isNeg ? "#991b1b" : isSelRow ? "#1d4ed8" : "#4b5563";

                return (
                  <div key={key} style={{ borderRadius: 4, background: rowBg, padding: rowBg !== "transparent" ? "2px 4px" : "0", margin: rowBg !== "transparent" ? "0 -4px" : "0" }}>
                    {/* Label + value */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 }}>
                      <span style={{
                        fontSize: 11, color: labelColor, fontWeight: isPos || isNeg || isSelRow ? 600 : 500,
                        flex: 1, paddingRight: 6,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        maxWidth: 220,
                      }}>
                        {displayLabel}
                      </span>
                      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1, flexShrink: 0 }}>
                        <span style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                          <span style={{
                            fontSize: 12, fontWeight: 700,
                            color: isNet ? (barDisplayNum >= 0 ? "#16a34a" : "#dc2626") : "#1a1d23",
                          }}>
                            {fmtVal(barDisplayNum)}
                          </span>
                          {showDelta && (
                            <span style={{
                              fontSize: 10, fontWeight: 700,
                              color: deltaVal > 0 ? "#16a34a" : deltaVal < 0 ? "#dc2626" : "#9ca3af",
                              background: deltaVal > 0 ? "rgba(22,163,74,0.1)" : deltaVal < 0 ? "rgba(220,38,38,0.1)" : "rgba(0,0,0,0.05)",
                              borderRadius: 3,
                              padding: "0 3px",
                            }}>
                              {deltaVal > 0 ? "↑" : deltaVal < 0 ? "↓" : "–"}{Math.abs(deltaVal).toFixed(1)}
                            </span>
                          )}
                        </span>
                        {Number.isFinite(optionCount) && (
                          <span style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", letterSpacing: "0.02em" }}>
                            N {optionCount.toLocaleString()}
                          </span>
                        )}
                      </span>
                    </div>
                    {/* Bar — shows absolute current value in compare mode */}
                    <div style={{ height: 3, background: "#f0f2f7", borderRadius: 3, position: "relative", overflow: "hidden" }}>
                      {isNet ? (
                        <div style={{
                          position: "absolute", top: 0, height: "100%",
                          left: barDisplayNum >= 0 ? "50%" : `${50 - barDisplayW / 2}%`,
                          width: `${barDisplayW / 2}%`,
                          background: barDisplayNum >= 0 ? "#16a34a" : "#dc2626",
                          borderRadius: 3,
                        }} />
                      ) : (
                        <div style={{
                          height: "100%", width: `${Math.max(0, barDisplayW)}%`,
                          background: isPos ? "#16a34a" : isNeg ? "#dc2626" : isSelRow ? "#2563eb" : barColor,
                          borderRadius: 3,
                          transition: "width 0.15s ease-out",
                        }} />
                      )}
                    </div>
                 </div>
               );
              })}
            </div>
          )}
        </div>

        {/* ── Net values below chart ── */}
        {(visibleBars.some((b) => b.key === "net1" || b.key === "Net") || (isCompare && Number.isFinite(netDelta))) && (
          <div style={{ padding: "6px 13px 8px", borderBottom: hasFooter ? "1px solid #f0f2f7" : "none" }}>
            {visibleBars.filter((b) => b.key === "net1" || b.key === "Net").map(({ key, label, value }) => {
              const num = Number(value);
              // In compare mode, num = delta. Get current net from latest row.
              const latestNet = isCompare && compareLatestRow && groupForCompare
                ? (() => {
                    const posKeys = toArrC(this.props.ActionMode === "filter" ? groupForCompare?.TargetVariable1?.polarity?.pos : groupForCompare?.polarity?.pos);
                    const negKeys = toArrC(this.props.ActionMode === "filter" ? groupForCompare?.TargetVariable1?.polarity?.neg : groupForCompare?.polarity?.neg);
                    const pos = sumKeysC(posKeys, compareLatestRow);
                    const neg = sumKeysC(negKeys, compareLatestRow);
                    return Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : null;
                  })()
                : null;
              const displayNum = isCompare && Number.isFinite(latestNet) ? latestNet : num;
              const barW = clampPct(Math.abs(displayNum));
              const netLabel = (key === "net1" || key === "Net") ? "Net" : (label || "Net");
              return (
                <div key={key}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 }}>
                    <span style={{ fontSize: 11, color: "#4b5563", fontWeight: 600 }}>
                      {netLabel}
                    </span>
                    <span style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                      <span style={{
                        fontSize: 12, fontWeight: 700,
                        color: displayNum >= 0 ? "#16a34a" : "#dc2626",
                      }}>
                        {fmtVal(displayNum)}
                      </span>
                      {isCompare && Number.isFinite(num) && (
                        <span style={{
                          fontSize: 10, fontWeight: 700,
                          color: num > 0 ? "#16a34a" : num < 0 ? "#dc2626" : "#9ca3af",
                          background: num > 0 ? "rgba(22,163,74,0.1)" : num < 0 ? "rgba(220,38,38,0.1)" : "rgba(0,0,0,0.05)",
                          borderRadius: 3,
                          padding: "0 3px",
                        }}>
                          {num > 0 ? "↑" : num < 0 ? "↓" : "–"}{Math.abs(num).toFixed(1)}
                        </span>
                      )}
                    </span>
                  </div>
                  <div style={{ height: 3, background: "#f0f2f7", borderRadius: 3, position: "relative", overflow: "hidden" }}>
                    <div style={{
                      position: "absolute", top: 0, height: "100%",
                      left: displayNum >= 0 ? "50%" : `${50 - barW / 2}%`,
                      width: `${barW / 2}%`,
                      background: displayNum >= 0 ? "#16a34a" : "#dc2626",
                      borderRadius: 3,
                    }} />
                  </div>
                </div>
              );
            })}
            {/* Standalone net delta row when group has polarity but net isn't in visibleBars */}
            {isCompare && Number.isFinite(netDelta) && !visibleBars.some(b => b.key === "net1" || b.key === "Net") && (() => {
              const latestNet = compareLatestRow && groupForCompare
                ? (() => {
                    const posKeys = toArrC(this.props.ActionMode === "filter" ? groupForCompare?.TargetVariable1?.polarity?.pos : groupForCompare?.polarity?.pos);
                    const negKeys = toArrC(this.props.ActionMode === "filter" ? groupForCompare?.TargetVariable1?.polarity?.neg : groupForCompare?.polarity?.neg);
                    const pos = sumKeysC(posKeys, compareLatestRow);
                    const neg = sumKeysC(negKeys, compareLatestRow);
                    return Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : null;
                  })()
                : null;
              const displayNum = Number.isFinite(latestNet) ? latestNet : netDelta;
              const barW = clampPct(Math.abs(displayNum));
              return (
                <div key="net-compare">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 }}>
                    <span style={{ fontSize: 11, color: "#4b5563", fontWeight: 600 }}>Net</span>
                    <span style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                      {Number.isFinite(latestNet) && (
                        <span style={{ fontSize: 12, fontWeight: 700, color: displayNum >= 0 ? "#16a34a" : "#dc2626" }}>
                          {fmtVal(displayNum)}
                        </span>
                      )}
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        color: netDelta > 0 ? "#16a34a" : netDelta < 0 ? "#dc2626" : "#9ca3af",
                        background: netDelta > 0 ? "rgba(22,163,74,0.1)" : netDelta < 0 ? "rgba(220,38,38,0.1)" : "rgba(0,0,0,0.05)",
                        borderRadius: 3,
                        padding: "0 3px",
                      }}>
                        {netDelta > 0 ? "↑" : netDelta < 0 ? "↓" : "–"}{Math.abs(netDelta).toFixed(1)}
                      </span>
                    </span>
                  </div>
                  <div style={{ height: 3, background: "#f0f2f7", borderRadius: 3, position: "relative", overflow: "hidden" }}>
                    <div style={{
                      position: "absolute", top: 0, height: "100%",
                      left: displayNum >= 0 ? "50%" : `${50 - barW / 2}%`,
                      width: `${barW / 2}%`,
                      background: displayNum >= 0 ? "#16a34a" : "#dc2626",
                      borderRadius: 3,
                    }} />
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── Footer ── */}
        {hasFooter && (
          <div style={{ padding: "7px 13px 9px", background: "#fafbfc" }}>

            {/* Stats row */}
            {nStats.length > 0 && (
              <div style={{
                display: "flex", alignItems: "center", flexWrap: "wrap",
                gap: "4px 0", marginBottom: benchStats.length > 0 ? 6 : 0,
              }}>
                {nStats.map(({ label, value, blue }, i) => (
                  <React.Fragment key={label}>
                    {i > 0 && (
                      <div style={{ width: 1, height: 10, background: "#d1d5db", margin: "0 6px", flexShrink: 0 }} />
                    )}
                    <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
                      <span style={{
                        fontSize: 9, fontWeight: 700,
                        color: blue ? "#2563eb" : "#9ca3af",
                        textTransform: "uppercase", letterSpacing: "0.06em",
                      }}>
                        {label}
                      </span>
                      <span style={{
                        fontSize: 11, fontWeight: 700,
                        color: blue ? "#2563eb" : "#374151",
                      }}>
                        {value}
                      </span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}

            {benchStats.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px 0" }}>
                {benchStats.map(({ label, value, blue }, i) => (
                  <React.Fragment key={label}>
                    {i > 0 && (
                      <div style={{ width: 1, height: 10, background: "#d1d5db", margin: "0 6px", flexShrink: 0 }} />
                    )}
                    <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
                      <span style={{
                        fontSize: 9, fontWeight: 700,
                        color: blue ? "#2563eb" : "#9ca3af",
                        textTransform: "uppercase", letterSpacing: "0.06em",
                      }}>
                        {label}
                      </span>
                      <span style={{
                        fontSize: 11, fontWeight: 700,
                        color: blue ? "#2563eb" : "#374151",
                      }}>
                        {value}
                      </span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}

          </div>
        )}
      </div>
    </div>,
    document.body
  );
})()}




      {!this.props.hideToolbar && <MapToolbar
        geoLevel={geoLevel}
        onGeoLevelChange={this.handleGeoLevelChange}
        selectedStateName={this.getSelectedStateName()} 
        allowNationalStateleg={allowNationalStatelegForTheme(this.props.selectedSection)}
        selectedAreaLabel={this.state.selectedFeature?.name || null}  
        clearSelection={this.clearSelection}                        
        zoomIn={() => this.mapRef.current?.getMap().zoomIn()}
        zoomOut={() => this.mapRef.current?.getMap().zoomOut()}
        resetView={this.resetView}
        mode={this.props.mode}
        onModeChange={(m) => this.props.onModeChange?.(m)}
        filters={this.props.filters || {}}
        onFiltersChange={(f) => this.props.onFiltersChange?.(f)}
        goBackOneLevel={this.goBackOneLevel} 
        poliInfo={this.state.poliInfo}
        hoverArea={this.props.hoverArea} 
        poliInfoToplevel={this.props.poliInfoToplevel}
        selectedFeature={this.state.selectedFeature || null}  
        selectedDemographics={this.props.selectedDemographics}  
        onClearDemographics={this.props.onClearDemographics}
        PossibleAreas={this.props.PossibleAreas}
        handleselectedFeatureChange={this.handleselectedFeatureChange}
        allowedLevels={allowedLevels}
        waveSelect={this.props.waveSelect}
        handlewaveSelectChange={this.props.handlewaveSelectChange}
        active_group={this.props.ActionMode === "filter" ? active_group?.TargetVariable1 : active_group}
        colorMap={this.props.colorMap}
        waveOptions={this.props.waveOptions}
        compareWaves={this.props.compareWaves}
        onCompareWavesChange={this.props.onCompareWavesChange}
        compareKey={this.props.compareKey}
        onCompareKeyChange={this.props.onCompareKeyChange}
        showArrows={this.state.showArrows}
        onShowArrowsToggle={() => this.setState(s => ({ showArrows: !s.showArrows }))}
        metricRows = {this.state.metricRows}
        geoPreloadStatus={this.state.geoPreloadStatus}
      />}

      </ReactMap>
      </div>
    );
  }
}

export default withRouter(MapPage);
